import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import "../TradePage/trade_new.css";
import "./margin.css";
import "../futures_trade/futures_trade_extras.css";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import { usePlatformStatus } from "../../../context/PlatformStatusProvider";
import { SocketContext } from "../../../customComponents/SocketContext";
import { Helmet } from "react-helmet-async";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import TradePairsSection from "./components/TradePairsSection";
import TradeCenterSection from "./components/TradeCenterSection";
import TradeHistorySection from "./components/TradeHistorySection";
import { enrichMarginAccounts, mergeMarginAccountSnapshots } from "./marginLevelUtils";
import {
    MARGIN_CLASSIC_SPOT_FORM_KINDS,
    MARGIN_SPOT_API_ORDER_KINDS,
    marginOrderUsesStopTriggerField,
    marginShowAmtDenomSelect,
} from "./marginConditionalOrderMenu";
import { FuturesTradeTransferProvider } from "../futures_trade/FuturesTradeTransferContext";
import { amountLimitLabel, resolveSubmittedBaseQty, snapToIncrement } from "../TradePage/spotQtyPrecision";

const DEFAULT_ORDER_BOOK_AGG_OPTIONS = [0.1, 0.5, 1, 10, 100];
const ORDER_BOOK_BOTH_DEPTH = 10;

/** Margin history tab: polling / infinite scroll constants. */
const SPOT_HIST_POLL_MS = 2000;
const SPOT_HIST_SCROLL_TOP_EPS = 6;
const SPOT_HIST_LOAD_MORE_PX = 96;
function getOrderBookAggOptionsForPair(tickSize) {
    const tick = Number(tickSize);
    if (!Number.isFinite(tick) || tick <= 0) {
        return DEFAULT_ORDER_BOOK_AGG_OPTIONS.slice();
    }
    const mults = [1, 10, 100, 1000, 10000];
    const out = [];
    for (const m of mults) {
        const v = tick * m;
        if (!Number.isFinite(v) || v <= 0) continue;
        out.push(parseFloat(v.toPrecision(12)));
    }
    const unique = Array.from(new Set(out)).sort((a, b) => a - b);
    return unique.length ? unique : DEFAULT_ORDER_BOOK_AGG_OPTIONS.slice();
}

function formatOrderBookNotionalShort(notional) {
    const n = Number(notional);
    if (!Number.isFinite(n)) return "0";
    const abs = Math.abs(n);
    if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    if (abs >= 1) return n.toFixed(2);
    return n.toFixed(4);
}

function roundPriceToAgg(price, agg) {
    const n = Number(price);
    if (!Number.isFinite(n) || !agg) return n;
    return Math.round(n / agg) * agg;
}

function aggregateOrderBookRows(orders, agg) {
    if (!orders?.length) return [];
    const map = new Map();
    for (const o of orders) {
        const bucket = roundPriceToAgg(o.price, agg);
        const prev = map.get(bucket);
        if (prev) {
            prev.quantity = (Number(prev.quantity) || 0) + (Number(o.quantity) || 0);
            prev.remaining = (Number(prev.remaining) || 0) + (Number(o.remaining) || 0);
        } else {
            map.set(bucket, { ...o, price: bucket });
        }
    }
    return Array.from(map.values());
}

/** Binance-style order book header: Price | Amount | Total + swap (columns can swap). */
function OrderBookHeaderRow({ quoteCurrency, baseCurrency, swap, onSwap }) {
    const q = quoteCurrency || "USDT";
    const b = baseCurrency || "BTC";
    return (
        <tr>
            <th className="orderbook-th-sticky orderbook-th-price">Price ({q})</th>
            {!swap ? (
                <>
                    <th className="orderbook-th-sticky text-center orderbook-th-amount">Amount ({b})</th>
                    <th className="orderbook-th-sticky text-end orderbook-th-total">
                        <span className="orderbook-th-total-inner">
                            <span>Total ({q})</span>
                            <button type="button" className="orderbook-col-swap-btn" onClick={onSwap} aria-label="Swap amount and total">
                                <i className="ri-arrow-left-right-line" aria-hidden />
                            </button>
                        </span>
                    </th>
                </>
            ) : (
                <>
                    <th className="orderbook-th-sticky text-end orderbook-th-total">
                        <span className="orderbook-th-total-inner">
                            <span>Total ({q})</span>
                            <button type="button" className="orderbook-col-swap-btn" onClick={onSwap} aria-label="Swap amount and total">
                                <i className="ri-arrow-left-right-line" aria-hidden />
                            </button>
                        </span>
                    </th>
                    <th className="orderbook-th-sticky text-center orderbook-th-amount">Amount ({b})</th>
                </>
            )}
        </tr>
    );
}

function OrderBookBidRow({ data, fill, rowTotal, swap, onRowClick, formatOrderBookAggPrice, formatQuantity, formatOrderBookNotionalShort }) {
    const price = formatOrderBookAggPrice(data.price);
    const amt = formatQuantity(data.remaining);
    const depth = (
        <>
            <span className="orderbook-depth-fill orderbook-depth-fill--bid" style={{ width: `${fill}%` }} />
            <span className="orderbook-depth-text">{formatOrderBookNotionalShort(rowTotal)}</span>
        </>
    );
    const amountCell = <td className="text-center orderbook-col-num">{amt}</td>;
    const totalCell = <td className="text-end orderbook-depth-cell orderbook-depth-cell--bid">{depth}</td>;
    return (
        <tr style={{ cursor: "pointer" }} onClick={onRowClick}>
            <td className="orderbook-col-price orderbook-col-price--bid">{price}</td>
            {!swap ? (
                <>
                    {amountCell}
                    {totalCell}
                </>
            ) : (
                <>
                    {totalCell}
                    {amountCell}
                </>
            )}
        </tr>
    );
}

function OrderBookAskRow({ data, fill, rowTotal, swap, onRowClick, formatOrderBookAggPrice, formatQuantity, formatOrderBookNotionalShort }) {
    const price = formatOrderBookAggPrice(data.price);
    const amt = formatQuantity(data.remaining);
    const depth = (
        <>
            <span className="orderbook-depth-fill orderbook-depth-fill--ask" style={{ width: `${fill}%` }} />
            <span className="orderbook-depth-text">{formatOrderBookNotionalShort(rowTotal)}</span>
        </>
    );
    const amountCell = <td className="text-center orderbook-col-num">{amt}</td>;
    const totalCell = <td className="text-end orderbook-depth-cell orderbook-depth-cell--ask">{depth}</td>;
    return (
        <tr style={{ cursor: "pointer" }} onClick={onRowClick}>
            <td className="orderbook-col-price orderbook-col-price--ask">{price}</td>
            {!swap ? (
                <>
                    {amountCell}
                    {totalCell}
                </>
            ) : (
                <>
                    {totalCell}
                    {amountCell}
                </>
            )}
        </tr>
    );
}

/** accent: "market" = green/red from last move; "bid"/"ask" = buy/sell tab colors (Binance-style) */
function OrderBookMidPriceRow({
    buyprice,
    isPricePositive,
    priceChange,
    formatPriceThousands,
    showChangePct = true,
    afterList = false,
    accent = "market",
}) {
    const useGreenAccent = accent === "bid" ? true : accent === "ask" ? false : isPricePositive;
    return (
        <div
            className={`mrkt_trde_tab justify-content-between orderbook-mid-price orderbook-mid-price--ss ${afterList ? "orderbook-mid-price--after-list" : ""}`}
        >
            <div className="d-flex align-items-center gap-2 flex-wrap db_bl">
                <b className={useGreenAccent ? "text-green orderbook-mid-main" : "text-danger orderbook-mid-main"}>{formatPriceThousands(buyprice)}</b>
                <i className={`ri-arrow-${isPricePositive ? "up" : "down"}-line orderbook-mid-arrow ${useGreenAccent ? "text-green" : "text-danger"}`} />
                <span className="text-muted orderbook-mid-usd">${formatPriceThousands(buyprice)}</span>
                {showChangePct ? <span className="text-muted small orderbook-mid-pct">{Number(priceChange ?? 0).toFixed(2)}%</span> : null}
            </div>
            <div className="arrowright_icon">
                <i className="ri-arrow-right-s-line" />
            </div>
        </div>
    );
}

// function OrderBookBidAskFooter({ bidPct, askPct }) {
//     return (
//         <div className="orderbook_bid_ask_footer">
//             <div className="orderbook_bid_ask_labels">
//                 <span className="text-green">B {bidPct.toFixed(2)}%</span>
//                 <span className="text-danger">{askPct.toFixed(2)}% S</span>
//             </div>
//             <div className="orderbook_bid_ask_bar" aria-hidden>
//                 <span style={{ flex: bidPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--bid" />
//                 <span style={{ flex: askPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--ask" />
//             </div>
//         </div>
//     );
// }

function OrderBookBidAskFooter({ bidPct, askPct }) {
    return (
        <div className="orderbook_bid_ask_footer">
            <div className="orderbook_bid_ask_labels">
                <span className="text-green">B {bidPct.toFixed(2)}%</span>
                <span className="text-danger">{askPct.toFixed(2)}% S</span>
            </div>
            <div className="orderbook_bid_ask_bar" aria-hidden>
                <span style={{ flex: bidPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--bid" />
                <span style={{ flex: askPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--ask" />
            </div>
        </div>
    );
}

const SPOT_OPEN_ORDER_KINDS = [
    { id: "all", label: "All" },
    { id: "limit", label: "Limit" },
    { id: "market", label: "Market" },
];

/** Quote suffixes for compact pair symbols e.g. BTCUSDT → BTC/USDT */
const SPOT_OPEN_ORDER_PAIR_QUOTES = [
    "USDT", "USDC", "FDUSD", "BUSD", "TUSD", "USDD", "DAI", "BTC", "ETH", "BNB", "SOL", "TRX", "TRY", "EUR", "GBP",
];

function spotOpenOrderMarketLabel(item, selectedBase, selectedQuote) {
    if (!item) return "---/---";
    const compact = item.pair != null ? String(item.pair).trim().toUpperCase() : "";
    if (compact) {
        for (const q of SPOT_OPEN_ORDER_PAIR_QUOTES) {
            if (compact.endsWith(q) && compact.length > q.length) {
                return `${compact.slice(0, -q.length)}/${q}`;
            }
        }
        return compact;
    }
    const ask = item?.ask_currency || selectedBase || "---";
    const pay = item?.pay_currency || selectedQuote || "---";
    return item?.side === "SELL" ? `${pay}/${ask}` : `${ask}/${pay}`;
}

function spotOpenOrderTimestamp(item) {
    return item?.created_at || item?.updatedAt || item?.createdAt;
}

function spotOpenOrderFilledQty(item) {
    const v = item?.filled_quantity ?? item?.filled;
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function spotOpenOrderRemainingQty(item) {
    const v = item?.remaining_quantity ?? item?.remaining;
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function spotOpenOrderTypeLabel(item) {
    const t = item?.order_type || item?.type;
    if (t == null || String(t).trim() === "") return "—";
    return String(t).toUpperCase();
}

function spotOpenOrderTifLabel(item) {
    const tif = item?.time_in_force;
    if (tif == null || String(tif).trim() === "") return "—";
    return String(tif).toUpperCase();
}

function matchesOpenOrderKind(item, kind) {
    if (kind === "all") return true;
    const t = String(item?.type || "").toUpperCase();
    if (kind === "limit") return t === "LIMIT";
    if (kind === "market") return t === "MARKET";
    return true;
}

function formatSpotOpenOrderDateTime(item, format) {
    const ts = spotOpenOrderTimestamp(item);
    if (ts == null || ts === "") return "—";
    const m = moment(ts);
    return m.isValid() ? m.format(format) : "—";
}

/** Order history / executed orders (exchange payload uses `pair`, `*_quantity`, `executed_value`, etc.) */
function orderHistoryMarketLabel(item) {
    return spotOpenOrderMarketLabel(item, item?.ask_currency, item?.pay_currency);
}

function orderHistoryEventTime(item) {
    return item?.updated_at || item?.updatedAt || item?.created_at || item?.createdAt;
}

function formatOrderHistoryDateTime(item, format) {
    const ts = orderHistoryEventTime(item);
    if (ts == null || ts === "") return "—";
    const m = moment(ts);
    return m.isValid() ? m.format(format) : "—";
}

function orderHistoryQuoteCurrency(item) {
    const lbl = orderHistoryMarketLabel(item);
    const parts = lbl.split("/");
    if (parts.length === 2 && parts[1] && parts[1] !== "---") return parts[1];
    return item?.pay_currency || "";
}

function orderHistoryExecutedValueNum(item) {
    if (item?.executed_value != null && String(item.executed_value).trim() !== "") {
        const n = Number(item.executed_value);
        if (Number.isFinite(n)) return n;
    }
    const fill = spotOpenOrderFilledQty(item);
    const avg = Number(item?.avg_execution_price);
    if (Number.isFinite(avg) && fill > 0) return fill * avg;
    return null;
}

function orderHistoryStatusClass(status) {
    const s = String(status || "").toUpperCase();
    if (s === "FILLED" || s === "EXECUTED") return "success";
    if (s === "CANCELLED" || s === "REJECTED" || s === "EXPIRED") return "danger";
    if (s === "PARTIALLY_FILLED" || s === "PARTIALLY FILLED") return "warning";
    return "warning";
}

function orderHistoryStatusLabel(status) {
    const s = String(status || "").toUpperCase();
    if (s === "FILLED") return "EXECUTED";
    if (!status || String(status).trim() === "") return "—";
    return status;
}

/** Unwrap MongoDB $numberDecimal objects to plain string values. */
const dec = (v) => (v != null && typeof v === "object" && v.$numberDecimal != null ? v.$numberDecimal : v);

/** Normalize a raw margin position history item from the API response. */
function normalizeMarginPositionHistoryItem(raw) {
    if (!raw) return raw;
    return {
        ...raw,
        quantity:               dec(raw.quantity),
        entry_price:            dec(raw.entry_price),
        notional:               dec(raw.notional),
        liquidation_price:      dec(raw.liquidation_price),
        close_price:            dec(raw.close_price),
        realized_pnl:           dec(raw.realized_pnl),
        total_fees:             dec(raw.total_fees),
        liq_fee:                dec(raw.liq_fee),
        total_interest_charged: dec(raw.total_interest_charged),
    };
}

/** Normalize a raw margin order history item from the API response. */
function normalizeMarginOrderItem(raw) {
    if (!raw) return raw;
    const qty    = parseFloat(dec(raw.quantity)        || 0);
    const filled = parseFloat(dec(raw.filled_quantity) || 0);
    const fillPct = qty > 0 ? `${((filled / qty) * 100).toFixed(2)}%` : "0%";
    return {
        ...raw,
        price:               dec(raw.price),
        quantity:            dec(raw.quantity),
        filled_quantity:     dec(raw.filled_quantity),
        remaining_quantity:  dec(raw.remaining_quantity),
        avg_execution_price: dec(raw.avg_execution_price),
        executed_value:      dec(raw.executed_value),
        locked_amount:       dec(raw.locked_amount),
        total_fee:           dec(raw.total_fee),
        total_tds:           dec(raw.total_tds),
        fill_percent:        fillPct,
        executed_prices: (Array.isArray(raw.executions) ? raw.executions : []).map((ex) => ({
            ...ex,
            price:          dec(ex.price),
            quantity:       dec(ex.quantity),
            quote_quantity: dec(ex.quote_quantity),
            fee:            dec(ex.fee),
            tds:            dec(ex.tds),
        })),
    };
}

/** Exchange spot payloads use `user_status`; legacy uses `status`. */
function spotOrderLifecycleStatus(item) {
    const s = item?.user_status ?? item?.status;
    if (s == null || String(s).trim() === "") return "";
    return String(s);
}

/**
 * Trade-history fills — each entry represents a single match between two
 * orders as returned by `GET /spot/v1/trades/my`. Payload shape:
 *   { _id, pair, side, price, quantity, quote_quantity,
 *     fee, fee_asset, fee_rate, tds, tds_asset, tds_rate,
 *     is_maker, order_id, is_self_match, executed_at }
 */
function tradeHistoryMarketLabel(item) {
    return spotOpenOrderMarketLabel(item);
}

function tradeHistoryBaseAsset(item) {
    return tradeHistoryMarketLabel(item).split("/")[0] || "";
}

function tradeHistoryQuoteAsset(item) {
    const parts = tradeHistoryMarketLabel(item).split("/");
    return parts.length === 2 ? parts[1] : "";
}

function formatTradeHistoryDateTime(item, format) {
    const ts = item?.executed_at || item?.executedAt || item?.created_at;
    if (ts == null || ts === "") return "—";
    const m = moment(ts);
    return m.isValid() ? m.format(format) : "—";
}

function tradeHistoryRoleLabel(item) {
    if (item?.is_maker === true) return "Maker";
    if (item?.is_maker === false) return "Taker";
    return "—";
}

const Margin = () => {
    const { getStatus } = usePlatformStatus();
    const isSpotDisabled = !getStatus('spot_trading').enabled;
    let params = useParams()
    let URL = params?.pairs?.split('_');

    const token = localStorage.getItem('token');
    const [urlPath, setUrlPath] = useState(URL ? URL : []);
    const [search, setsearch] = useState('');
    const [AllData, setAllData] = useState({});
    const [BuyOrders, setBuyOrders] = useState([]);
    const [CoinPairDetails, setCoinPairDetails] = useState();
    const [RecentTrade, setRecentTrade] = useState([]);
    const [SellOrders, setSellOrders] = useState([]);
    const [buyamount, setbuyamount] = useState("");
    const [sellAmount, setsellAmount] = useState("");
    /** BASE | QUOTE — Market, Stop Market, Stop Limit (plain Limit stays base-only). */
    const [buyAmountDenom, setbuyAmountDenom] = useState("BASE");
    const [sellAmountDenom, setsellAmountDenom] = useState("BASE");
    const [buySlippageEnabled, setBuySlippageEnabled] = useState(false);
    const [buySlippageInput, setBuySlippageInput] = useState("");
    const [sellSlippageEnabled, setSellSlippageEnabled] = useState(false);
    const [sellSlippageInput, setSellSlippageInput] = useState("");
    const [placingOrderSide, setPlacingOrderSide] = useState("");
    const [infoPlaceOrder, setinfoPlaceOrder] = useState('LIMIT');
    const [coinFilter, setcoinFilter] = useState('ALL');
    const [BuyCoinBal, setBuyCoinBal] = useState(0);
    const [SellCoinBal, setSellCoinBal] = useState(0);
    const [marginBalance, setMarginBalance] = useState({ quoteLocked: 0, baseLocked: 0, quoteBorrowed: 0, baseBorrowed: 0 });
    const [openOrders, setopenOrders] = useState([]); // eslint-disable-line no-unused-vars
    const [marginOpenOrdersRows, setMarginOpenOrdersRows] = useState([]);
    const [marginOrderHistoryRows, setMarginOrderHistoryRows] = useState([]);
    const [marginPositionData, setMarginPositionData] = useState(null);
    const [posHistSideFilter, setPosHistSideFilter] = useState("All");
    const [posHistStartDate, setPosHistStartDate] = useState("");
    const [posHistEndDate, setPosHistEndDate] = useState("");
    const [posHistPreset, setPosHistPreset] = useState("");

    const [loanRows, setLoanRows] = useState([]);
    const [repayModalLoan, setRepayModalLoan] = useState(null);

    const [closePositionModal, setClosePositionModal] = useState(false);
    const [closePositionLoading, setClosePositionLoading] = useState(false);
    const [closingPositionPairs, setClosingPositionPairs] = useState({});
    const closingPositionTimersRef = useRef({});
    const [marginPositionHistoryRows, setMarginPositionHistoryRows] = useState([]);
    const [allMarginAccounts, setAllMarginAccounts] = useState([]);
    const [orderType, setorderType] = useState('All');
    const [pastOrderType, setpastOrderType] = useState('All');
    const [marginTradeHistoryRows, setMarginTradeHistoryRows] = useState([]);
    const [orderHistoryScrollPaused, setOrderHistoryScrollPaused] = useState(false); // eslint-disable-line no-unused-vars
    const [tradeHistoryScrollPaused, setTradeHistoryScrollPaused] = useState(false);
    const [tradeHistorySideFilter, setTradeHistorySideFilter] = useState('All');
    const [ohSideFilter, setOhSideFilter] = useState("All");
    const [ohPreset, setOhPreset] = useState("");
    const [ohStartDate, setOhStartDate] = useState("");
    const [ohEndDate, setOhEndDate] = useState("");
    const [thPreset, setThPreset] = useState("");
    const [thStartDate, setThStartDate] = useState("");
    const [thEndDate, setThEndDate] = useState("");
    const [favCoins, setfavCoins] = useState([]);
    const [sellOrderPrice, setsellOrderPrice] = useState('');
    const [buyOrderPrice, setbuyOrderPrice] = useState('');
    const [buyStopPrice, setBuyStopPrice] = useState('');
    const [sellStopPrice, setSellStopPrice] = useState('');
    const [leverageConfirmed, setLeverageConfirmed] = useState(0);
    const [leverageDraft, setLeverageDraft] = useState(0);
    const [stopBuyPercent, setStopBuyPercent] = useState(0);
    const [stopSellPercent, setStopSellPercent] = useState(0);
    const [limitBuyPercent, setLimitBuyPercent] = useState(0);
    const [limitBuyFok, setLimitBuyFok] = useState(false);
    const [limitBuyIoc, setLimitBuyIoc] = useState(false);
    const [limitSellPercent, setLimitSellPercent] = useState(0);
    const [limitSellFok, setLimitSellFok] = useState(false);
    const [limitSellIoc, setLimitSellIoc] = useState(false);
    const [marketBuyPercent, setMarketBuyPercent] = useState(0);
    const [marketSellPercent, setMarketSellPercent] = useState(0);
    const [priceChange, setpriceChange] = useState('');
    const [changesHour, setChangesHour] = useState('');
    const [priceHigh, setpriceHigh] = useState('');
    const [priceLow, setpriceLow] = useState('');
    const [volume, setvolume] = useState('');
    const [showCoinList, setShowCoinList] = useState(false);
    /** True after socket sends order book fields (`buy_order` / `sell_order`) for the active pair. */
    const [orderBookSocketReady, setOrderBookSocketReady] = useState(false);
    const [baseCurId, setbaseCurId] = useState(null);
    const [quoteCurId, setquoteCurId] = useState(null);
    /** Reference bid/ask at pair load or pair change only (limit input fallback, sizing, market ref). */
    const [buyprice, setbuyprice] = useState('');
    const [sellPrice, setsellPrice] = useState('');
    /** Live last price for header / order book mid; does not drive the limit price field. */
    const [lastMarketPrice, setlastMarketPrice] = useState('');
    const [SelectedCoin, setSelectedCoin] = useState();
    const [isPricePositive, setIsPricePositive] = useState(true);
    /** Previous stream bid for last-move color; reset on pair change. */
    const lastStreamBidRef = useRef(null);
    const [showTab, setShowTab] = useState("chart");
    const [showBuySellTab, setShowBuySellTab] = useState("");
    const [orderBookActiveTab, setOrderBookActiveTab] = useState("orderbook");
    const [positionOrderTab, setPositionOrderTab] = useState("size");
    const [openOrderKindTab, setOpenOrderKindTab] = useState("all");
    const [Coins, setCoins] = useState([]);
    const [expandedRowIndex, setExpandedRowIndex] = useState(null);
    const [showExecutedTrades, setShowExecutedTrades] = useState({});
    const marginOrderHistPageRef = useRef(0);
    const marginOrderHistHasMoreRef = useRef(true);
    const marginOrderHistInFlightRef = useRef(false);
    const marginOrderHistScopeRef = useRef(0);
    const marginTradeHistPageRef = useRef(0);
    const marginTradeHistHasMoreRef = useRef(true);
    const marginTradeHistInFlightRef = useRef(false);
    const marginTradeHistScopeRef = useRef(0);
    /** Reset TP/SL draft when margin pair (base|quote) changes. */
    const marginTpSlPairKeySeenRef = useRef(null);
    const [showMobileFavouritesPopup, setShowMobileFavouritesPopup] = useState(false);
    const [isFavouritesOpen, setIsFavouritesOpen] = useState(false);
    const [isConditionalMenuOpen, setIsConditionalMenuOpen] = useState(false);

    // -------------------------------------------------------------------------
    // MARGIN — TP/SL conditional branch (`infoPlaceOrder === "TP_SL"`)
    // Fields: TP trigger (≤), TP limit (or market), SL trigger (≥), SL limit (or market), amount, % slider.
    // -------------------------------------------------------------------------
    const [marginFormTpSl, setMarginFormTpSl] = useState({
        tpTriggerPrice: "",
        tpLimitMarket: true,
        tpLimitPrice: "",
        slTriggerPrice: "",
        slLimitMarket: true,
        slLimitPrice: "",
        quantity: "",
        amountPercent: 0,
    });

    // -------------------------------------------------------------------------
    // MARGIN — TWAP branch (`infoPlaceOrder === "TWAP"`)
    // -------------------------------------------------------------------------
    const [marginFormTwap, setMarginFormTwap] = useState({
        totalQty: "",
        totalPercent: 0,
        durationHours: "",
        durationMinutes: "",
        priceVariance: "",
        priceVarianceUnit: "%",
        activationPrice: "",
        timeInterval: "",
        timeIntervalUnit: "Hour",
        perSubOrderQty: "",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Iceberg Pro branch (`infoPlaceOrder === "ICEBERG_PRO"`)
    // -------------------------------------------------------------------------
    const [marginFormIcebergPro, setMarginFormIcebergPro] = useState({
        priceVariance: "",
        priceVarianceUnit: "%",
        activationPrice: "",
        totalQty: "",
        perSubOrderQty: "",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Loop order branch (`infoPlaceOrder === "LOOP_ORDER"`)
    // -------------------------------------------------------------------------
    const [marginFormLoopOrder, setMarginFormLoopOrder] = useState({
        price: "",
        priceMode: "LAST_PRICE",
        takeProfitPct: "",
        stopLossPct: "",
        pauseAfterSlMin: "",
        amount: "",
        totalLoops: "",
        validFor: "1Day",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Trailing stop branch (`infoPlaceOrder === "TRAILING_STOP"`)
    // -------------------------------------------------------------------------
    const [marginFormTrailingStop, setMarginFormTrailingStop] = useState({
        pullbackRatePct: "",
        pullbackUnit: "%",
        activationPrice: "",
        amount: "",
        amountPercent: 0,
        validFor: "1Day",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Advanced limit branch (`infoPlaceOrder === "ADVANCED_LIMIT"`)
    // -------------------------------------------------------------------------
    const [marginFormAdvancedLimit, setMarginFormAdvancedLimit] = useState({
        limitPrice: "",
        amount: "",
        amountPercent: 0,
        total: "",
        orderType: "POST_ONLY",
        tpSl: false,
        iceberg: false,
        tpPrice: "",
        slPrice: "",
        icebergQty: "",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Generic conditional (`infoPlaceOrder === "CONDITIONAL"`)
    // -------------------------------------------------------------------------
    const [marginFormConditional, setMarginFormConditional] = useState({
        triggerPrice: "",
        orderMarket: true,
        orderLimitPrice: "",
        amount: "",
        amountPercent: 0,
        validFor: "Permanent",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Percentage of Volume (POV) (`infoPlaceOrder === "POV"`)
    // -------------------------------------------------------------------------
    const [marginFormPov, setMarginFormPov] = useState({
        amount: "",
        targetParticipationRate: "Low",
        validFor: "1 Days",
        triggerPrice: "",
        priceLimit: "",
        advancedOptionsOpen: false,
    });

    const { userDetails } = useContext(ProfileContext);
    const KycStatus = userDetails?.kycVerified;
    const { isConnected, exchangeData, subscribeToExchange, unsubscribeFromExchange } = useContext(SocketContext);
    const [orderBookAggStep, setOrderBookAggStep] = useState(DEFAULT_ORDER_BOOK_AGG_OPTIONS[0]);
    const [orderBookAggOpen, setOrderBookAggOpen] = useState(false);
    /** both = split book, bids = buy only, asks = sell only */
    const [orderBookViewMode, setOrderBookViewMode] = useState("both");
    /** Swap Amount ↔ Total column order (Binance-style). */
    const [orderBookSwapAmountTotal, setOrderBookSwapAmountTotal] = useState(false);
    const [priceFieldFocus, setPriceFieldFocus] = useState(null);
    const [spotWallets, setSpotWallets] = useState([]);
    const [walletsLoading, setWalletsLoading] = useState(false);
    const navigate = useNavigate();

    const selectedPairApiCompact = useMemo(() => {
        if (!SelectedCoin?.base_currency || !SelectedCoin?.quote_currency) return "";
        const b = String(SelectedCoin.base_currency).trim();
        const q = String(SelectedCoin.quote_currency).trim();
        if (!b || !q) return "";
        return `${b}${q}`.toUpperCase();
    }, [SelectedCoin?.base_currency, SelectedCoin?.quote_currency]);

    const selectedPairApiMargin = useMemo(() => {
        if (!SelectedCoin?.base_currency || !SelectedCoin?.quote_currency) return "";
        return `${String(SelectedCoin.base_currency).toUpperCase()}${String(SelectedCoin.quote_currency).toUpperCase()}`;
    }, [SelectedCoin?.base_currency, SelectedCoin?.quote_currency]);

    const slippageBounds = useMemo(() => {
        const minRaw = Number(SelectedCoin?.min_slippage_percent);
        const maxRaw = Number(SelectedCoin?.max_slippage_percent);
        const min = Number.isFinite(minRaw) && minRaw > 0 ? minRaw : 0.1;
        const max = Number.isFinite(maxRaw) && maxRaw >= min ? maxRaw : Math.max(1, min);
        return { min, max };
    }, [SelectedCoin?.min_slippage_percent, SelectedCoin?.max_slippage_percent]);
    const slippagePlaceholder = `Allowed ${slippageBounds.min}% - ${slippageBounds.max}%`;

    const currentMarginAccount = useMemo(
        () => allMarginAccounts.find((a) => a.pair_id === SelectedCoin?._id) || null,
        [allMarginAccounts, SelectedCoin?._id]
    );

    const getSlippageInputError = (enabled, rawInput) => {
        if (!enabled) return "";
        const text = String(rawInput ?? "").trim();
        if (text === "") return "";
        const n = Number(text);
        if (!Number.isFinite(n)) return "Enter a valid slippage percent.";
        if (n < slippageBounds.min || n > slippageBounds.max) {
            return `Slippage must be between ${slippageBounds.min}% and ${slippageBounds.max}%.`;
        }
        return "";
    };
    const buySlippageError = getSlippageInputError(buySlippageEnabled, buySlippageInput);
    const sellSlippageError = getSlippageInputError(sellSlippageEnabled, sellSlippageInput);

    const coinIconSrc = (path) => {
        if (!path) return "/images/coin_placeholder.png";
        const s = String(path);
        if (s.startsWith("http") || s.startsWith("/images/")) return s;
        return `${resolvePublicUploadUrl(s.replace(/^\//, ""))}`;
    };

    const safeToFixed8 = (value, fallback = "0") => {
        const parsed = parseFloat(value);
        if (!Number.isFinite(parsed)) return fallback;
        const s = parsed.toFixed(8).replace(/\.?0+$/, "");
        return s === "" ? "0" : s;
    };

    const getOpenOrderStopPrice = (item) => {
        const raw = item?.stop_price ?? item?.stopPrice;
        if (raw == null || String(raw).trim() === "") return "—";
        return safeToFixed8(raw, "—");
    };

    const getOpenOrderPair = (item) =>
        spotOpenOrderMarketLabel(item, SelectedCoin?.base_currency, SelectedCoin?.quote_currency);



    const reloadMarginOpenOrders = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        try {
            const res = await AuthService.marginOpenOrders({ page: 1, limit: 100 });
            if (res?.success) {
                const items = Array.isArray(res.data)
                    ? res.data
                    : (Array.isArray(res.data?.items) ? res.data.items : []);
                setMarginOpenOrdersRows(items);
            }
        } catch {
            /* keep existing rows */
        }
    }, [token]);

    const applyOhPreset = useCallback((preset) => {
        const today = new Date();
        const start = new Date();
        const daysMap = { "1D": 1, "7D": 7, "30D": 30, "90D": 90 };
        start.setDate(today.getDate() - (daysMap[preset] || 0));
        const toLocalIso = (d) => {
            const y = d.getFullYear();
            const mo = String(d.getMonth() + 1).padStart(2, "0");
            const dy = String(d.getDate()).padStart(2, "0");
            return `${y}-${mo}-${dy}`;
        };
        setOhStartDate(toLocalIso(start));
        setOhEndDate(toLocalIso(today));
        setOhPreset(preset);
    }, []);

    const reloadMarginOrderHistory = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        const scope = ++marginOrderHistScopeRef.current;
        try {
            const res = await AuthService.marginOrderHistory({
                page: 1,
                limit: 20,
                side: ohSideFilter !== "All" ? ohSideFilter : undefined,
                start_date: ohStartDate || undefined,
                end_date: ohEndDate || undefined,
            });
            if (marginOrderHistScopeRef.current !== scope) return;
            if (res?.success) {
                const raw = (Array.isArray(res.data) ? res.data : []).map(normalizeMarginOrderItem);
                setMarginOrderHistoryRows((prev) => {
                    const freshIds = new Set(raw.map((r) => String(r._id)));
                    const tail = (prev || []).filter((r) => !freshIds.has(String(r._id)));
                    return [...raw, ...tail];
                });
                if (marginOrderHistPageRef.current <= 1) {
                    marginOrderHistPageRef.current = raw.length ? 1 : 0;
                    marginOrderHistHasMoreRef.current = res.pagination?.has_more ?? raw.length >= 20;
                }
            }
        } catch {
            /* keep existing rows */
        }
    }, [token, ohSideFilter, ohStartDate, ohEndDate]);

    const appendMarginOrderHistory = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        if (!marginOrderHistHasMoreRef.current || marginOrderHistInFlightRef.current) return;
        if (marginOrderHistPageRef.current < 1) return;
        const scope = marginOrderHistScopeRef.current;
        const nextPage = marginOrderHistPageRef.current + 1;
        marginOrderHistInFlightRef.current = true;
        try {
            const res = await AuthService.marginOrderHistory({
                page: nextPage,
                limit: 20,
                side: ohSideFilter !== "All" ? ohSideFilter : undefined,
                start_date: ohStartDate || undefined,
                end_date: ohEndDate || undefined,
            });
            if (marginOrderHistScopeRef.current !== scope) return;
            if (res?.success) {
                const raw = (Array.isArray(res.data) ? res.data : []).map(normalizeMarginOrderItem);
                if (raw.length === 0) { marginOrderHistHasMoreRef.current = false; return; }
                setMarginOrderHistoryRows((prev) => {
                    const existing = new Set((prev || []).map((r) => String(r._id)));
                    return [...(prev || []), ...raw.filter((r) => !existing.has(String(r._id)))];
                });
                marginOrderHistPageRef.current = nextPage;
                marginOrderHistHasMoreRef.current = res.pagination?.has_more ?? raw.length >= 20;
            }
        } finally {
            marginOrderHistInFlightRef.current = false;
        }
    }, [token, ohSideFilter, ohStartDate, ohEndDate]);

    const handleMarginOrderHistoryScroll = useCallback(
        (e) => {
            const el = e?.currentTarget;
            if (!(el instanceof Element)) return;
            const paused = el.scrollTop > SPOT_HIST_SCROLL_TOP_EPS;
            setOrderHistoryScrollPaused((prev) => (prev === paused ? prev : paused));
            if (!marginOrderHistHasMoreRef.current || marginOrderHistInFlightRef.current) return;
            const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (gap < SPOT_HIST_LOAD_MORE_PX) void appendMarginOrderHistory();
        },
        [appendMarginOrderHistory]
    );

    const applyThPreset = useCallback((preset) => {
        const today = new Date();
        const start = new Date();
        const daysMap = { "1D": 1, "7D": 7, "30D": 30, "90D": 90 };
        start.setDate(today.getDate() - (daysMap[preset] || 0));
        const toLocalIso = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        };
        setThStartDate(toLocalIso(start));
        setThEndDate(toLocalIso(today));
        setThPreset(preset);
    }, []);

    const reloadMarginTradeHistory = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        const scope = ++marginTradeHistScopeRef.current;
        try {
            const res = await AuthService.marginTrades({
                page: 1,
                limit: 20,
                side: tradeHistorySideFilter !== "All" ? tradeHistorySideFilter : undefined,
                start_date: thStartDate || undefined,
                end_date: thEndDate || undefined,
            });
            if (marginTradeHistScopeRef.current !== scope) return;
            if (res?.success) {
                const raw = Array.isArray(res.data) ? res.data : [];
                setMarginTradeHistoryRows((prev) => {
                    const freshIds = new Set(raw.map((r) => String(r.trade_id)));
                    const tail = (prev || []).filter((r) => !freshIds.has(String(r.trade_id)));
                    return [...raw, ...tail];
                });
                if (marginTradeHistPageRef.current <= 1) {
                    marginTradeHistPageRef.current = raw.length ? 1 : 0;
                    marginTradeHistHasMoreRef.current = res.pagination?.has_more ?? raw.length >= 20;
                }
            }
        } catch {
            /* keep existing rows */
        }
    }, [token, tradeHistorySideFilter, thStartDate, thEndDate]);

    const appendMarginTradeHistory = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        if (!marginTradeHistHasMoreRef.current || marginTradeHistInFlightRef.current) return;
        if (marginTradeHistPageRef.current < 1) return;
        const scope = marginTradeHistScopeRef.current;
        const nextPage = marginTradeHistPageRef.current + 1;
        marginTradeHistInFlightRef.current = true;
        try {
            const res = await AuthService.marginTrades({
                page: nextPage,
                limit: 20,
                side: tradeHistorySideFilter !== "All" ? tradeHistorySideFilter : undefined,
                start_date: thStartDate || undefined,
                end_date: thEndDate || undefined,
            });
            if (marginTradeHistScopeRef.current !== scope) return;
            if (res?.success) {
                const raw = Array.isArray(res.data) ? res.data : [];
                if (raw.length === 0) { marginTradeHistHasMoreRef.current = false; return; }
                setMarginTradeHistoryRows((prev) => {
                    const existing = new Set((prev || []).map((r) => String(r.trade_id)));
                    return [...(prev || []), ...raw.filter((r) => !existing.has(String(r.trade_id)))];
                });
                marginTradeHistPageRef.current = nextPage;
                marginTradeHistHasMoreRef.current = res.pagination?.has_more ?? raw.length >= 20;
            }
        } finally {
            marginTradeHistInFlightRef.current = false;
        }
    }, [token, tradeHistorySideFilter, thStartDate, thEndDate]);

    const handleMarginTradeHistoryScroll = useCallback(
        (e) => {
            const el = e?.currentTarget;
            if (!(el instanceof Element)) return;
            const paused = el.scrollTop > SPOT_HIST_SCROLL_TOP_EPS;
            setTradeHistoryScrollPaused((prev) => (prev === paused ? prev : paused));
            if (!marginTradeHistHasMoreRef.current || marginTradeHistInFlightRef.current) return;
            const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (gap < SPOT_HIST_LOAD_MORE_PX) void appendMarginTradeHistory();
        },
        [appendMarginTradeHistory]
    );

    const reloadMarginPosition = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        try {
            const res = await AuthService.marginPositions();
            if (res?.success) {
                const positions = Array.isArray(res.data) ? res.data : [];
                setMarginPositionData(positions.length > 0 ? positions : null);
            }
        } catch {
            /* keep existing */
        }
    }, [token]);

    const reloadMarginPositionHistory = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        try {
            const res = await AuthService.marginPositionHistory(undefined, {
                page: 1,
                limit: 50,
                ...(posHistStartDate ? { start_date: posHistStartDate } : {}),
                ...(posHistEndDate   ? { end_date:   posHistEndDate   } : {}),
            });
            if (res?.success) {
                const raw = (Array.isArray(res.data)
                    ? res.data
                    : (Array.isArray(res.data?.items) ? res.data.items : [])
                ).map(normalizeMarginPositionHistoryItem);
                setMarginPositionHistoryRows(raw);
            }
        } catch {
            /* keep existing rows */
        }
    }, [token, posHistStartDate, posHistEndDate]);

    useEffect(() => {
        if (!token || positionOrderTab !== "loanManagement") return;
        AuthService.marginLoans({})
            .then((res) => {
                if (res?.success) setLoanRows(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {});
    }, [token, positionOrderTab]);

    const markPositionClosing = useCallback((pair) => {
        if (!pair) return;
        if (closingPositionTimersRef.current[pair]) {
            clearTimeout(closingPositionTimersRef.current[pair]);
        }
        setClosingPositionPairs((prev) => ({ ...prev, [pair]: true }));
        closingPositionTimersRef.current[pair] = setTimeout(() => {
            setClosingPositionPairs((prev) => {
                if (!prev[pair]) return prev;
                const next = { ...prev };
                delete next[pair];
                return next;
            });
            delete closingPositionTimersRef.current[pair];
        }, 10000);
    }, []);

    useEffect(() => () => {
        Object.values(closingPositionTimersRef.current).forEach(clearTimeout);
        closingPositionTimersRef.current = {};
    }, []);

    const handleClosePosition = useCallback(async () => {
        // closePositionModal holds the pair string of the position being closed
        const pair = typeof closePositionModal === "string" ? closePositionModal : selectedPairApiMargin;
        if (!pair || closePositionLoading || closingPositionPairs[pair]) return;
        setClosePositionLoading(true);
        try {
            const res = await AuthService.marginClosePosition(pair, { type: "MARKET" });
            if (res?.success) {
                alertSuccessMessage("Position close order placed.");
                setClosePositionModal(false);
                markPositionClosing(pair);
                void reloadMarginPosition();
            } else {
                alertErrorMessage(res?.message || "Failed to close position.");
            }
        } catch {
            alertErrorMessage("Failed to close position.");
        } finally {
            setClosePositionLoading(false);
        }
    }, [closePositionModal, selectedPairApiMargin, closePositionLoading, closingPositionPairs, markPositionClosing, reloadMarginPosition]);

    useEffect(() => {
        marginOrderHistScopeRef.current += 1;
        marginTradeHistScopeRef.current += 1;
        setMarginOrderHistoryRows([]);
        setMarginTradeHistoryRows([]);
        marginOrderHistPageRef.current = 0;
        marginTradeHistPageRef.current = 0;
        marginOrderHistHasMoreRef.current = true;
        marginTradeHistHasMoreRef.current = true;
        setExpandedRowIndex(null);
        setOrderHistoryScrollPaused(false);
        setTradeHistoryScrollPaused(false);
    }, [selectedPairApiCompact]);

    useEffect(() => {
        marginTradeHistScopeRef.current += 1;
        marginTradeHistPageRef.current = 0;
        marginTradeHistHasMoreRef.current = true;
        setMarginTradeHistoryRows([]);
    }, [tradeHistorySideFilter, thStartDate, thEndDate]);

    useEffect(() => {
        marginOrderHistScopeRef.current += 1;
        marginOrderHistPageRef.current = 0;
        marginOrderHistHasMoreRef.current = true;
        setMarginOrderHistoryRows([]);
    }, [ohSideFilter, ohStartDate, ohEndDate]);

    useEffect(() => {
        if (params?.pairs) {
            setUrlPath(params.pairs.split("_"));
        }
    }, [params?.pairs]);

    useEffect(() => {
        if (!orderBookAggOpen) return undefined;
        const onDown = (e) => {
            if (!e.target.closest?.(".orderbook_agg_dd")) {
                setOrderBookAggOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOrderBookAggOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [orderBookAggOpen]);

    const orderBookAggOptions = useMemo(
        () => getOrderBookAggOptionsForPair(SelectedCoin?.tick_size),
        [SelectedCoin?.tick_size]
    );

    // Always use the finest aggregation step (tick) for the pair = most order book rows / maximum detail
    useEffect(() => {
        if (!orderBookAggOptions.length) return;
        setOrderBookAggStep(orderBookAggOptions[0]);
    }, [orderBookAggOptions, SelectedCoin?.base_currency_id, SelectedCoin?.quote_currency_id]);

    useEffect(() => {
        if (!exchangeData) return;

        if (exchangeData?.balance) {
            const b = exchangeData.balance;
            setBuyCoinBal(parseFloat(b.quote_currency_balance) || 0);
            setSellCoinBal(parseFloat(b.base_currency_balance) || 0);
            setMarginBalance({
                quoteLocked:              parseFloat(b.quote_currency_locked)    || 0,
                baseLocked:               parseFloat(b.base_currency_locked)     || 0,
                quoteBorrowed:            parseFloat(b.quote_currency_borrowed)  || 0,
                baseBorrowed:             parseFloat(b.base_currency_borrowed)   || 0,
                netEquity:                parseFloat(b.net_equity)               || null,
                marginLevel:              b.margin_level != null && b.margin_level !== ""
                    ? parseFloat(b.margin_level)
                    : null,
                baseRemainingCapacity:    parseFloat(b.base_remaining_capacity)  ?? null,
                quoteRemainingCapacity:   parseFloat(b.quote_remaining_capacity) ?? null,
            });
            if (b.margin_level != null && b.margin_level !== "" && SelectedCoin?._id) {
                const liveMl = parseFloat(b.margin_level);
                if (Number.isFinite(liveMl)) {
                    setAllMarginAccounts((prev) => mergeMarginAccountSnapshots(
                        prev,
                        [{
                            pair_id: SelectedCoin._id,
                            pair: `${SelectedCoin.base_currency}${SelectedCoin.quote_currency}`,
                            margin_level: liveMl,
                            base_borrowed: b.base_currency_borrowed,
                            quote_borrowed: b.quote_currency_borrowed,
                        }]
                    ));
                }
            }
        }

        const hasBuyArr = Array.isArray(exchangeData?.buy_order);
        const hasSellArr = Array.isArray(exchangeData?.sell_order);
        if (hasBuyArr) {
            setBuyOrders(exchangeData.buy_order);
        }
        if (hasSellArr) {
            setSellOrders(exchangeData.sell_order);
        }
        // Only flip ready once we've received an actual order book array for the
        // current pair (empty array is still "ready"; missing key is not).
        if (hasBuyArr || hasSellArr) {
            setOrderBookSocketReady(true);
        }

        if (exchangeData?.open_orders !== undefined) {
            setopenOrders(exchangeData.open_orders || []);
        }

        if (exchangeData?.recent_trades) {
            setRecentTrade(exchangeData.recent_trades);
        }

        if (exchangeData?.ticker) {
            const t = exchangeData.ticker;
            if (t?.buy_price != null) {
                const p = t.buy_price;
                if (lastStreamBidRef.current != null) {
                    const current = Number(p);
                    const previous = Number(lastStreamBidRef.current);
                    if (current > previous) {
                        setIsPricePositive(true);
                    } else if (current < previous) {
                        setIsPricePositive(false);
                    }
                }
                lastStreamBidRef.current = p;
                setlastMarketPrice(p);
            }
            if (t?.change != null) setpriceChange(t.change);
            if (t?.change_24hour != null) setChangesHour(t.change_24hour);
            if (t?.high != null) setpriceHigh(t.high);
            if (t?.low != null) setpriceLow(t.low);
            if (t?.volume != null) setvolume(t.volume);
        }

        if (exchangeData?.pairs) {
            setCoins(exchangeData.pairs);
            setAllData((prev) => ({
                ...prev,
                ...exchangeData,
                pairs: exchangeData.pairs,
            }));
        } else {
            setAllData((prev) => ({ ...prev, ...exchangeData }));
        }
    }, [exchangeData]);

    useEffect(() => {
        if (!baseCurId || !quoteCurId || !isConnected) return;
        subscribeToExchange(baseCurId, quoteCurId, {
            tradeType: "margin",
            pairId:    SelectedCoin?._id,
        });
        return () => {
            unsubscribeFromExchange(baseCurId, quoteCurId);
        };
    }, [baseCurId, quoteCurId, isConnected, SelectedCoin?._id, subscribeToExchange, unsubscribeFromExchange]);

    useEffect(() => {
        // Pair switch: clear stale data so previous pair's values don't flash on new pair
        setOrderBookSocketReady(false);
        setBuyOrders([]);
        setSellOrders([]);
        setbuyprice('');
        setlastMarketPrice('');
        setbuyOrderPrice('');
        setsellOrderPrice('');
    }, [baseCurId, quoteCurId]);

    useEffect(() => {
        if (!SelectedCoin && CoinPairDetails && CoinPairDetails.length > 0 && isConnected) {
            let Pair;
            let filteredData;
            if (urlPath?.length > 0) {
                filteredData = CoinPairDetails?.filter?.((item) => {
                    return urlPath[0]?.includes(item?.base_currency) && urlPath[1]?.includes(item?.quote_currency);
                });
            }
            if (filteredData?.length > 0) {
                Pair = filteredData[0];
            } else {
                Pair = CoinPairDetails[0];
            }
            if (!Pair) return;
            navigate(`/margin/${Pair?.base_currency}_${Pair?.quote_currency}`);
            setsellOrderPrice("");
            setbuyOrderPrice("");
            setSelectedCoin(Pair);
            setbaseCurId(Pair?.base_currency_id);
            setquoteCurId(Pair?.quote_currency_id);
            setbuyprice(Pair?.buy_price);
            setsellPrice(Pair?.sell_price);
            lastStreamBidRef.current = null;
            setlastMarketPrice(Pair?.buy_price);
            setbuyamount("");
            setsellAmount("");
            setBuyStopPrice("");
            setSellStopPrice("");
            setBuySlippageEnabled(false);
            setBuySlippageInput("");
            setSellSlippageEnabled(false);
            setSellSlippageInput("");
            setStopBuyPercent(0);
            setStopSellPercent(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CoinPairDetails, isConnected, urlPath, navigate]);

    useEffect(() => {
        if (isConnected && !CoinPairDetails?.length) {
            subscribeToExchange();
        }
    }, [isConnected, CoinPairDetails?.length, subscribeToExchange]);

    const pastOrders = useMemo(() => {
        const list = (marginOrderHistoryRows || []).filter(
            (item) => pastOrderType === item?.side || pastOrderType === "All"
        );
        return [...list].sort((a, b) => {
            const ta = Date.parse(a?.created_at || a?.updated_at || a?.created || "") || 0;
            const tb = Date.parse(b?.created_at || b?.updated_at || b?.created || "") || 0;
            return tb - ta;
        });
    }, [pastOrderType, marginOrderHistoryRows]);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || positionOrderTab !== "orderHistory") return undefined;
        if (!selectedPairApiMargin) return undefined;

        void reloadMarginOrderHistory();
        const id = window.setInterval(() => void reloadMarginOrderHistory(), SPOT_HIST_POLL_MS);
        return () => window.clearInterval(id);
    }, [
        token,
        positionOrderTab,
        selectedPairApiMargin,
        reloadMarginOrderHistory,
    ]);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || positionOrderTab !== "positions") return undefined;
        if (!selectedPairApiMargin) return undefined;

        void reloadMarginOpenOrders();
        const id = window.setInterval(() => void reloadMarginOpenOrders(), 3000);
        return () => window.clearInterval(id);
    }, [
        token,
        positionOrderTab,
        selectedPairApiMargin,
        reloadMarginOpenOrders,
    ]);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || positionOrderTab !== "size") return undefined;
        if (!selectedPairApiMargin) return undefined;

        void reloadMarginPosition();
        const id = window.setInterval(() => void reloadMarginPosition(), 3000);
        return () => window.clearInterval(id);
    }, [
        token,
        positionOrderTab,
        selectedPairApiMargin,
        reloadMarginPosition,
    ]);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || positionOrderTab !== "positionHistory") return undefined;
        if (!selectedPairApiMargin) return undefined;

        void reloadMarginPositionHistory();
        const id = window.setInterval(() => void reloadMarginPositionHistory(), SPOT_HIST_POLL_MS);
        return () => window.clearInterval(id);
    }, [
        token,
        positionOrderTab,
        selectedPairApiMargin,
        posHistStartDate,
        posHistEndDate,
        reloadMarginPositionHistory,
    ]);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || positionOrderTab !== "tradeHistory") return undefined;
        if (tradeHistoryScrollPaused || !selectedPairApiMargin) return undefined;

        void reloadMarginTradeHistory();
        const id = window.setInterval(() => void reloadMarginTradeHistory(), SPOT_HIST_POLL_MS);
        return () => window.clearInterval(id);
    }, [
        token,
        positionOrderTab,
        tradeHistoryScrollPaused,
        selectedPairApiMargin,
        reloadMarginTradeHistory,
    ]);

    // ********* Update Buy Sell 24HChange High Low Volume Price********** //
    useEffect(() => {
        let filteredData = AllData?.pairs?.filter((item) => {
            return item?.base_currency_id === SelectedCoin?.base_currency_id
        })
        if (filteredData?.length) {
            setpriceChange(filteredData[0]?.change_percentage);
            setChangesHour(filteredData[0]?.change);
            setpriceHigh(filteredData[0]?.high);
            setpriceLow(filteredData[0]?.low);
            setvolume(filteredData[0]?.volume);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AllData]);


    // ********* Update Buy Sell 24HChange High Low Volume Price********** //
    useEffect(() => {
        if (AllData && SelectedCoin) {
            let filteredData = AllData?.pairs?.filter((item) => {
                return item?.base_currency_id === SelectedCoin?.base_currency_id && item?.quote_currency_id === SelectedCoin?.quote_currency_id
            })
            if (filteredData?.length) {
                const p = filteredData[0]?.buy_price;
                if (p != null) {
                    if (lastStreamBidRef.current != null) {
                        const current = Number(p);
                        const previous = Number(lastStreamBidRef.current);
                        if (current > previous) {
                            setIsPricePositive(true);
                        } else if (current < previous) {
                            setIsPricePositive(false);
                        }
                    }
                    lastStreamBidRef.current = p;
                    setlastMarketPrice(p);
                }
                setpriceChange(filteredData[0]?.change_percentage);
                setChangesHour(filteredData[0]?.change);
                setpriceHigh(filteredData[0]?.high);
                setpriceLow(filteredData[0]?.low);
                setvolume(filteredData[0]?.volume);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AllData]);


    // ********* Search Coins ********** //
    useEffect(() => {
        let filteredData = AllData?.pairs?.filter((item) => {
            return item?.base_currency?.toLowerCase().includes(search?.toLowerCase()) || item?.quote_currency?.toLowerCase().includes(search?.toLowerCase())
        })
        setCoinPairDetails(filteredData)
    }, [search, AllData]);

    // Set default coin filter to first quote currency
    useEffect(() => {
        if (CoinPairDetails?.length > 0 && coinFilter === 'ALL') {
            const firstQuoteCurrency = CoinPairDetails[0]?.quote_currency;
            if (firstQuoteCurrency) {
                setcoinFilter(firstQuoteCurrency);
            }
        }
    }, [CoinPairDetails, coinFilter]);



    const fetchSpotWallets = async () => {
        if (!token) return;
        setWalletsLoading(true);
        try {
            const result = await AuthService.getUserfunds("spot");
            if (result?.success) {
                const walletsWithBalance = (result.data || [])
                    .filter((w) => parseFloat(w.balance) > 0)
                    .sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance));
                setSpotWallets(walletsWithBalance);
            }
        } catch (error) {
            console.error("Failed to fetch spot wallets:", error);
        } finally {
            setWalletsLoading(false);
        }
    };

    useEffect(() => {
        if (infoPlaceOrder === "MARKET") {
            return;
        }
        if (buyAmountDenom === "QUOTE") setbuyamount("");
        if (sellAmountDenom === "QUOTE") setsellAmount("");
        setbuyAmountDenom("BASE");
        setsellAmountDenom("BASE");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [infoPlaceOrder]);

    /** Margin UI no longer offers Stop Limit / Stop Market — normalize legacy tab state. */
    useEffect(() => {
        setinfoPlaceOrder((prev) => (prev === "STOP_LIMIT" || prev === "STOP_MARKET" ? "LIMIT" : prev));
    }, []);


    const cancelOrder = async (orderId) => {
        try {
            const result = await AuthService.marginCancelOrder(orderId);
            if (result?.success) {
                alertSuccessMessage("Order Cancelled Successfully");
            } else if (result?.status === 409 || result?.code === "ORDER_ALREADY_TERMINAL") {
                alertErrorMessage("Order is already filled or cancelled.");
            } else {
                alertErrorMessage(result?.message || "Failed to cancel order.");
            }
            if (selectedPairApiMargin) {
                AuthService.marginOpenOrders({ pair: selectedPairApiMargin }).then((res) => {
                    const items = res?.success
                        ? (Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []))
                        : [];
                    setMarginOpenOrdersRows(items);
                }).catch(() => {});
            }
        } catch {
            alertErrorMessage("Failed to cancel order.");
        }
    };

    const handleAddFav = async (pairId) => {
        LoaderHelper.loaderStatus(true);
        await AuthService.favoriteCoin(pairId).then((result) => {
            if (result?.success) {
                LoaderHelper.loaderStatus(false);
                favoriteList();
            } else {
                LoaderHelper.loaderStatus(false);
                alertErrorMessage(result?.message);
            }
        });
    };
    const favoriteList = async () => {
        await AuthService.favoriteList().then((result) => {
            if (result?.success) {
                setfavCoins(result?.data?.pairs ? result?.data?.pairs : ['']);
            }
        });
    };

    const fetchAllMarginAccounts = useCallback(async ({ fullEnrich = true } = {}) => {
        try {
            const res = await AuthService.marginAccounts();
            if (!res?.data || !Array.isArray(res.data)) return;
            if (fullEnrich) {
                const enriched = await enrichMarginAccounts(res.data);
                setAllMarginAccounts(enriched);
                return;
            }
            setAllMarginAccounts((prev) => mergeMarginAccountSnapshots(prev, res.data));
        } catch (_) { /* non-critical */ }
    }, []);

    const fetchAllMarginAccountsRef = useRef(fetchAllMarginAccounts);
    fetchAllMarginAccountsRef.current = fetchAllMarginAccounts;

    const marginAccountSocketRefreshRef = useRef(null);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || !SelectedCoin?._id) return;
        AuthService.marginAccount(SelectedCoin._id)
            .then((res) => {
                if (!res?.success || !res.data) return;
                setAllMarginAccounts((prev) => mergeMarginAccountSnapshots(
                    prev,
                    [{ ...res.data, pair_id: SelectedCoin._id }]
                ));
            })
            .catch(() => {});
    }, [SelectedCoin?._id, token]);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || positionOrderTab !== "size") return undefined;
        let cancelled = false;
        const refreshLevels = () => {
            const fetchAccounts = fetchAllMarginAccountsRef.current;
            if (cancelled || typeof fetchAccounts !== "function") return;
            void fetchAccounts({ fullEnrich: false });
        };
        refreshLevels();
        const id = window.setInterval(refreshLevels, 3000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [token, positionOrderTab]);

    useEffect(() => {
        if (DISABLE_ALL_NETWORK || !token || !SelectedCoin?._id || !exchangeData?.balance) return undefined;
        if (marginAccountSocketRefreshRef.current) {
            window.clearTimeout(marginAccountSocketRefreshRef.current);
        }
        marginAccountSocketRefreshRef.current = window.setTimeout(() => {
            AuthService.marginAccount(SelectedCoin._id)
                .then((res) => {
                    if (!res?.success || !res.data) return;
                    setAllMarginAccounts((prev) => mergeMarginAccountSnapshots(
                        prev,
                        [{ ...res.data, pair_id: SelectedCoin._id }]
                    ));
                })
                .catch(() => {});
        }, 500);
        return () => {
            if (marginAccountSocketRefreshRef.current) {
                window.clearTimeout(marginAccountSocketRefreshRef.current);
            }
        };
    }, [exchangeData?.balance, SelectedCoin?._id, token]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        token && favoriteList();
        token && fetchSpotWallets();
        token && fetchAllMarginAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Reset orderbook when switching pairs
    const resetOrderbook = () => {
        // Flip the loading flag back FIRST so the next render shows the spinner
        // instead of an empty "No Orders" frame (the pair-change useEffect would
        // otherwise only catch up one render later).
        setOrderBookSocketReady(false);
        setSellOrders([]);
        setBuyOrders([]);
        setRecentTrade([]);
        setOrderBookViewMode("both");
        setOrderBookSwapAmountTotal(false);
    };

    const handleSelectCoin = (data) => {
        // Skip if selecting the same pair - don't reset or re-subscribe
        if (SelectedCoin?.base_currency_id === data?.base_currency_id &&
            SelectedCoin?.quote_currency_id === data?.quote_currency_id) {
            setShowCoinList(false);
            return;
        }

        resetOrderbook();
        setinfoPlaceOrder("LIMIT");
        navigate(`/margin/${data?.base_currency}_${data?.quote_currency}`);
        setsellOrderPrice('');
        setbuyOrderPrice('');
        setSelectedCoin(data);
        setbaseCurId(data?.base_currency_id);
        setquoteCurId(data?.quote_currency_id);
        setbuyprice(data?.buy_price);
        setsellPrice(data?.sell_price);
        lastStreamBidRef.current = null;
        setlastMarketPrice(data?.buy_price);
        setShowCoinList(false);
        setbuyamount("");
        setsellAmount("");
        setBuyStopPrice("");
        setSellStopPrice("");
        setBuySlippageEnabled(false);
        setBuySlippageInput("");
        setSellSlippageEnabled(false);
        setSellSlippageInput("");
        setStopBuyPercent(0);
        setStopSellPercent(0);
        setLimitBuyPercent(0);
        setLimitSellPercent(0);
        setMarketBuyPercent(0);
        setMarketSellPercent(0);
        setbuyAmountDenom("BASE");
        setsellAmountDenom("BASE");
        setExpandedRowIndex(null);
        let meta = Coins?.find(
            (item) => item?.short_name === data?.base_currency || item?.base_currency === data?.base_currency
        );
        setDesAndLinks({ ...(meta || {}), description: meta?.description || "", links: meta?.links || [] });
    };

    const [desAndLinks, setDesAndLinks] = useState({ description: "", links: [] });
    const getDescAndLink = () => {
        if (SelectedCoin) {
            let meta = Coins?.find(
                (item) => item?.short_name === SelectedCoin?.base_currency || item?.base_currency === SelectedCoin?.base_currency
            );
            setDesAndLinks({ ...(meta || {}), description: meta?.description || "", links: meta?.links || [] });
        }
    }

    const nineDecimalFormat = (data) => {
        if (typeof (data) === "number") {
            // return data
            return parseFloat(data?.toFixed(9))
        } else {
            return 0
        }
    };

    // Get decimal places from tick_size or step_size
    const getDecimalPlaces = (value) => {
        if (!value || value >= 1) return 0;
        const str = value.toString();
        if (str.includes('e-')) {
            return parseInt(str.split('e-')[1]);
        }
        const decimalPart = str.split('.')[1];
        return decimalPart ? decimalPart.length : 0;
    };

    // Get price precision based on tick_size
    const getPricePrecision = () => {
        const tickSize = SelectedCoin?.tick_size;
        if (tickSize === undefined || tickSize === null) return 8; // default if no tick_size
        return getDecimalPlaces(tickSize);
    };

    // Get quantity precision based on step_size
    const getQuantityPrecision = () => {
        const stepSize = SelectedCoin?.step_size;
        if (stepSize === undefined || stepSize === null) return 8; // default if no step_size
        return getDecimalPlaces(stepSize);
    };

    // Format price based on tick_size precision
    const formatPrice = (price) => {
        if (price === undefined || price === null || isNaN(price)) return '0';
        const precision = getPricePrecision();
        return parseFloat(Number(price).toFixed(precision));
    };

    // Format quantity based on step_size precision
    const formatQuantity = (qty) => {
        if (qty === undefined || qty === null || isNaN(qty)) return '0';
        const precision = getQuantityPrecision();
        return parseFloat(Number(qty).toFixed(precision));
    };

    const [orderFieldError, setOrderFieldError] = useState(null);
    const showFieldError = (side, field, text) => setOrderFieldError({ side, field, text });
    useEffect(() => {
        setOrderFieldError((prev) => (prev?.field === "amount" ? null : prev));
    }, [buyamount, sellAmount]);
    useEffect(() => {
        setOrderFieldError((prev) => (prev?.field === "price" ? null : prev));
    }, [buyOrderPrice, sellOrderPrice]);

    // Validate order before placing
    const validateOrder = (price, quantity, side, amountIsQuote = false, orderKind) => { // eslint-disable-line no-unused-vars
        const tick_size = SelectedCoin?.tick_size || 0.01;
        const step_size = SelectedCoin?.step_size || 0.00001;
        const min_notional = parseMinNotional();
        const max_order_qty = SelectedCoin?.max_order_qty || 9000;

        const skipPriceTick = orderKind === "MARKET" || orderKind === "STOP_MARKET";

        const numPrice = parseFloat(price);
        const numQuantity = parseFloat(quantity);

        if (!Number.isFinite(numPrice)) {
            showFieldError(side, "price", "Enter price");
            return false;
        }
        if (!Number.isFinite(numQuantity)) {
            showFieldError(side, "amount", "Enter amount");
            return false;
        }

        const baseQty = amountIsQuote && numPrice > 0 ? numQuantity / numPrice : numQuantity;
        const total = amountIsQuote ? numQuantity : numPrice * numQuantity;

        // Validate price tick_size (limit / stop-limit; market uses server price)
        if (!skipPriceTick) {
            const pricePrecision = getDecimalPlaces(tick_size);
            const priceMultiplier = Math.pow(10, pricePrecision);
            if (Math.round(numPrice * priceMultiplier) % Math.round(tick_size * priceMultiplier) !== 0) {
                showFieldError(side, "price", "Invalid price");
                return false;
            }
        }

        if (amountIsQuote && numPrice <= 0) {
            showFieldError(side, "price", "Enter price");
            return false;
        }

        // Validate quantity step_size (base units)
        const qtyPrecision = getDecimalPlaces(step_size);
        const qtyMultiplier = Math.pow(10, qtyPrecision);
        if (Math.round(baseQty * qtyMultiplier) % Math.round(step_size * qtyMultiplier) !== 0) {
            showFieldError(side, "amount", "Invalid amount");
            return false;
        }

        // Validate max_order_qty (base)
        if (baseQty > max_order_qty) {
            const maxShown = amountIsQuote && numPrice > 0 ? max_order_qty * numPrice : max_order_qty;
            showFieldError(side, "amount", amountLimitLabel("Max", maxShown, amountIsQuote ? tick_size : step_size, amountIsQuote ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
            return false;
        }

        // Validate min_notional (minimum order value in quote)
        if (!Number.isFinite(total) || total < min_notional - 0.001) {
            const minShown = amountIsQuote ? min_notional : (numPrice > 0 ? min_notional / numPrice : min_notional);
            showFieldError(side, "amount", amountLimitLabel("Min", minShown, amountIsQuote ? tick_size : step_size, amountIsQuote ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
            return false;
        }

        // Validate insufficient funds
        if (side === "BUY") {
            const availableBalance = BuyCoinBal || 0;
            const spend = amountIsQuote ? numQuantity : total;
            if (spend > availableBalance) {
                const maxAmt = amountIsQuote
                    ? availableBalance
                    : (numPrice > 0 ? snapToIncrement(availableBalance / numPrice, step_size, "floor") : 0);
                showFieldError(side, "amount", amountLimitLabel("Max", maxAmt, amountIsQuote ? tick_size : step_size, amountIsQuote ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
                return false;
            }
        } else if (side === "SELL") {
            const availableBalance = SellCoinBal || 0;
            if (baseQty > availableBalance) {
                const maxAmt = amountIsQuote && numPrice > 0
                    ? availableBalance * numPrice
                    : snapToIncrement(availableBalance, step_size, "floor");
                showFieldError(side, "amount", amountLimitLabel("Max", maxAmt, amountIsQuote ? tick_size : step_size, amountIsQuote ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
                return false;
            }
        }

        return true;
    };

    const formatTotal = (value) => {
        if (value == null || !Number.isFinite(value)) return "0";
        if (value < 0) return value.toFixed(getPricePrecision()).replace(/\.?0+$/, '');
        const precision = getPricePrecision();
        const finalValue = value.toFixed(precision).replace(/\.?0+$/, '');
        const decimalPart = finalValue.split('.')[1];
        if (!decimalPart) return finalValue;
        let zeroCount = 0;
        for (let char of decimalPart) {
            if (char === '0') zeroCount++;
            else break;
        }
        const result = decimalPart.slice(zeroCount);
        if (zeroCount > 4 || value < 1e-7) {
            return `0.0{${zeroCount}}${result}`;
        }
        return finalValue;
    };

    const parseOrderQty = (s) => {
        const n = parseFloat(String(s ?? "").replace(/,/g, ""));
        return Number.isFinite(n) ? n : NaN;
    };

    const parsePriceNum = (v) => {
        const n = parseFloat(String(v ?? "").replace(/,/g, ""));
        return Number.isFinite(n) ? n : NaN;
    };

    const getStepSize = () => Number(SelectedCoin?.step_size) || 0.00001;

    /** Snap qty to step_size without float drift (e.g. 0.00007 must not floor to 0.00006). */
    const snapQtyToStep = (qty, mode = "floor") => {
        const step = getStepSize();
        if (!Number.isFinite(qty) || qty <= 0) return 0;
        const units = (qty / step);
        const scaled = Math.round(units * 1e12) / 1e12;
        const steps = mode === "ceil"
            ? Math.ceil(scaled - 1e-12)
            : Math.floor(scaled + 1e-12);
        const precision = getDecimalPlaces(step);
        return parseFloat((steps * step).toFixed(precision));
    };

    const toFixed8 = (data) => snapQtyToStep(data, "floor");

    /** Round qty up to step_size — used when deriving base qty from a quote total. */
    const ceilQuantityToStep = (qty) => snapQtyToStep(qty, "ceil");

    const parseMinNotional = () => {
        const raw = SelectedCoin?.min_notional;
        const n = typeof raw === "object" && raw?.$numberDecimal != null
            ? Number(raw.$numberDecimal)
            : Number(raw);
        return Number.isFinite(n) && n > 0 ? n : 5;
    };

    const roundQuoteNotional = (n) => {
        if (!Number.isFinite(n)) return NaN;
        const dp = SelectedCoin?.quote_decimal ?? getPricePrecision();
        return parseFloat(n.toFixed(dp));
    };

    const orderNotionalQuote = (numPrice, baseQty, raw, isQuoteSize) => {
        if (isQuoteSize) return roundQuoteNotional(parseOrderQty(raw));
        const price = parsePriceNum(numPrice);
        const qty = Number.isFinite(baseQty) ? baseQty : snapQtyToStep(parseOrderQty(raw), "floor");
        if (!Number.isFinite(price) || !Number.isFinite(qty)) return NaN;
        return roundQuoteNotional(price * qty);
    };

    const applySpotOrderType = (next) => {
        setinfoPlaceOrder(next);
        setOrderFieldError(null);
        if (next === "MARKET") {
            setsellAmountDenom("BASE");
        }
        if (next === "MARKET" || next === "STOP_MARKET") {
            setbuyOrderPrice("");
            setsellOrderPrice("");
        } else if (next === "LIMIT" || next === "STOP_LIMIT") {
            const live =
                Number(lastMarketPrice) ||
                Number(buyprice) ||
                Number(SelectedCoin?.buy_price) ||
                Number(SelectedCoin?.close) ||
                Number(sellPrice) ||
                Number(SelectedCoin?.sell_price) ||
                0;
            const formatted = formatPrice(live);
            const n = Number(formatted);
            if (Number.isFinite(n) && n > 0) {
                const str = String(formatted);
                setbuyOrderPrice(str);
                setsellOrderPrice(str);
            }
        }
    };

    const buildSpotOrderToApi = (side) => {
        const t = infoPlaceOrder;
        const isMarketT = t === "MARKET" || t === "STOP_MARKET";
        const isQuoteSize =
            (t === "MARKET" || t === "STOP_MARKET" || t === "STOP_LIMIT" || t === "CONDITIONAL") &&
            (side === "BUY" ? buyAmountDenom === "QUOTE" : sellAmountDenom === "QUOTE");
        const raw = side === "BUY" ? buyamount : sellAmount;

        const refForMarket = side === "BUY" ? (parsePriceNum(lastMarketPrice) || parsePriceNum(buyprice) || 0) : (parsePriceNum(lastMarketPrice) || parsePriceNum(sellPrice) || 0);
        const limitFromUi =
            side === "BUY"
                ? (String(buyOrderPrice).trim() !== "" ? parsePriceNum(buyOrderPrice) : NaN) || parsePriceNum(buyprice) || 0
                : (String(sellOrderPrice).trim() !== "" ? parsePriceNum(sellOrderPrice) : NaN) || parsePriceNum(sellPrice) || 0;

        const priceForValidation = isMarketT ? refForMarket : limitFromUi;

        const refForStopDefault = refForMarket;
        const stopPx =
            t === "STOP_LIMIT" || t === "STOP_MARKET" || t === "CONDITIONAL"
                ? (side === "BUY"
                    ? (String(buyStopPrice).trim() !== "" ? parsePriceNum(buyStopPrice) : refForStopDefault)
                    : (String(sellStopPrice).trim() !== "" ? parsePriceNum(sellStopPrice) : refForStopDefault))
                : null;

        const priceForApi = isMarketT ? refForMarket : limitFromUi;

        const n = parseOrderQty(raw);
        const sized = Number.isFinite(n) && n > 0
            ? resolveSubmittedBaseQty({
                amount: n,
                isQuote: isQuoteSize,
                price: priceForValidation,
                stepSize: SelectedCoin?.step_size,
                tickSize: SelectedCoin?.tick_size,
                minOrderQty: SelectedCoin?.min_order_qty,
                minNotional: parseMinNotional(),
                base: SelectedCoin?.base_currency,
                quote: SelectedCoin?.quote_currency,
            })
            : { ok: false, qty: NaN, message: "" };
        const baseQty = sized.qty;
        const sizeError = sized.ok ? "" : sized.message;

        const extras = {};
        if (stopPx != null && Number.isFinite(stopPx) && (t === "STOP_LIMIT" || t === "STOP_MARKET" || t === "CONDITIONAL")) {
            extras.stop_price = stopPx;
        }
        if (t === "LIMIT" || t === "STOP_LIMIT" || t === "CONDITIONAL") {
            if (side === "BUY") {
                if (limitBuyFok) extras.fok = true;
                if (limitBuyIoc) extras.ioc = true;
            } else {
                if (limitSellFok) extras.fok = true;
                if (limitSellIoc) extras.ioc = true;
            }
        }
        return { t, priceForValidation, priceForApi, baseQty, raw, isQuoteSize, extras, sizeError };
    };

    /**
     * Build a Spot Trading `/spot/v1/orders` payload from the current UI state.
     * Follows the Spot Trading Frontend Integration Guide:
     *   - `pair` is the uppercase engine symbol (e.g. "BTCUSDT").
     *   - `price` is only sent for LIMIT / STOP_LIMIT.
     *   - `stop_price` is only sent for STOP_LIMIT / STOP_MARKET.
     *   - FOK / IOC checkboxes map to `time_in_force`; default is `GTC`.
     *   - Decimal fields are sent as strings to avoid float precision loss.
     */
    const buildSpotOrderPayload = (side, t, priceForApi, baseQty, extras, marketBuyQuoteQty = null, leverage = null) => {
        const base = String(SelectedCoin?.base_currency || "").toUpperCase();
        const quote = String(SelectedCoin?.quote_currency || "").toUpperCase();
        const pair = base && quote ? `${base}_${quote}` : "";

        const payload = { pair, type: t, side };

        if (t === "MARKET" && side === "BUY" && marketBuyQuoteQty != null && marketBuyQuoteQty > 0) {
            payload.quote_quantity = String(marketBuyQuoteQty);
        } else {
            payload.quantity = String(baseQty);
        }

        if (t === "LIMIT" || t === "STOP_LIMIT") {
            payload.price = String(priceForApi);
        }
        if ((t === "STOP_LIMIT" || t === "STOP_MARKET") && extras?.stop_price != null) {
            payload.stop_price = String(extras.stop_price);
        }

        let tif = "GTC";
        if (t === "LIMIT" || t === "STOP_LIMIT") {
            if (extras?.fok) tif = "FOK";
            else if (extras?.ioc) tif = "IOC";
            else if (extras?.post_only) tif = "POST_ONLY";
        }
        if (tif !== "GTC") {
            payload.time_in_force = tif;
        }

        if (t === "MARKET" || t === "STOP_MARKET") {
            const slippageEnabled = side === "BUY" ? buySlippageEnabled : sellSlippageEnabled;
            const slippageInput = side === "BUY" ? buySlippageInput : sellSlippageInput;
            if (slippageEnabled && String(slippageInput).trim() !== "") {
                const slip = parseFloat(slippageInput);
                if (Number.isFinite(slip) && slip >= slippageBounds.min && slip <= slippageBounds.max) {
                    payload.max_slippage_percent = String(slip);
                }
            }
        }

        if (Number.isFinite(leverage) && leverage > 0) {
            payload.leverage = leverage;
        }

        return payload;
    };

    const MARGIN_ORDER_ERROR_MESSAGES = {
        INSUFFICIENT_FUNDS: "Insufficient funds.",
        BORROW_LIMIT_EXCEEDED: "Order exceeds your maximum borrowable amount.",
        ACCOUNT_LIQUIDATING: "Your account is under liquidation. No new orders can be placed.",
        "Duplicate order": "Duplicate order detected. Please wait before placing again.",
    };

    const placeSpotOrder = async (side, selectedLeverage = 1) => {
        if (!SelectedCoin?.base_currency_id || !SelectedCoin?.quote_currency_id) {
            alertErrorMessage("Select a trading pair");
            return;
        }
        if (!MARGIN_SPOT_API_ORDER_KINDS.has(infoPlaceOrder)) {
            alertErrorMessage(
                "This margin order mode is UI-only until the API supports it. Use Limit, Market, Stop Limit, or Stop Market to place an order."
            );
            return;
        }
        const { t, priceForValidation, priceForApi, baseQty, raw, isQuoteSize, extras, sizeError } = buildSpotOrderToApi(side);
        if (t === "MARKET" || t === "STOP_MARKET") {
            const slipErr = side === "BUY" ? buySlippageError : sellSlippageError;
            if (slipErr) return;
        }
        if (sizeError) {
            showFieldError(side, "amount", sizeError);
            return;
        }
        if (!Number.isFinite(baseQty) || baseQty <= 0) {
            showFieldError(side, "amount", "Enter amount");
            return;
        }

        const tick_size = SelectedCoin?.tick_size || 0.01;
        const step_size = SelectedCoin?.step_size || 0.00001;
        const min_notional = parseMinNotional();
        const max_order_qty = SelectedCoin?.max_order_qty || 9000;
        const skipPriceTick = t === "MARKET" || t === "STOP_MARKET";
        const numPrice = parsePriceNum(priceForValidation);
        if (!skipPriceTick) {
            if (!Number.isFinite(numPrice) || numPrice <= 0) {
                showFieldError(side, "price", "Enter price");
                return;
            }
            const pricePrecision = getDecimalPlaces(tick_size);
            const priceMultiplier = Math.pow(10, pricePrecision);
            if (Math.round(numPrice * priceMultiplier) % Math.round(tick_size * priceMultiplier) !== 0) {
                showFieldError(side, "price", "Invalid price");
                return;
            }
        }
        const qtyPrecision = getDecimalPlaces(step_size);
        const qtyMultiplier = Math.pow(10, qtyPrecision);
        if (Math.round(baseQty * qtyMultiplier) % Math.round(step_size * qtyMultiplier) !== 0) {
            showFieldError(side, "amount", "Invalid amount");
            return;
        }
        if (baseQty > max_order_qty) {
            const maxShown = isQuoteSize && numPrice > 0 ? max_order_qty * numPrice : max_order_qty;
            showFieldError(side, "amount", amountLimitLabel("Max", maxShown, isQuoteSize ? tick_size : step_size, isQuoteSize ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
            return;
        }
        const total = orderNotionalQuote(numPrice, baseQty, raw, isQuoteSize);
        if (!Number.isFinite(total) || total < min_notional - 0.001) {
            const minShown = isQuoteSize ? min_notional : (numPrice > 0 ? min_notional / numPrice : min_notional);
            showFieldError(side, "amount", amountLimitLabel("Min", minShown, isQuoteSize ? tick_size : step_size, isQuoteSize ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
            return;
        }

        const buyQuoteMax = (parseFloat(BuyCoinBal) || 0) * selectedLeverage;
        if (side === "BUY") {
            const orderCost = isQuoteSize ? parseOrderQty(raw) : total;
            if (Number.isFinite(buyQuoteMax) && Number.isFinite(orderCost) && orderCost > buyQuoteMax) {
                const maxAmt = isQuoteSize
                    ? buyQuoteMax
                    : (numPrice > 0 ? snapToIncrement(buyQuoteMax / numPrice, step_size, "floor") : 0);
                showFieldError(side, "amount", amountLimitLabel("Max", maxAmt, isQuoteSize ? tick_size : step_size, isQuoteSize ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
                return;
            }
        } else if (side === "SELL") {
            const valNE = (() => {
                const sNE = marginBalance.netEquity;
                if (sNE != null && Number.isFinite(sNE) && sNE >= 0) return sNE;
                const Qf = BuyCoinBal || 0, Bf = SellCoinBal || 0;
                const Qb = marginBalance.quoteBorrowed || 0, Bb = marginBalance.baseBorrowed || 0;
                const P0 = numPrice > 0 ? numPrice : (Number(buyprice) || 0);
                return Math.max(0, (Qf - Qb) + (Bf - Bb) * P0);
            })();
            const P = numPrice > 0 ? numPrice : (Number(sellPrice) || 0);
            const sellMaxBase = P > 0 ? (valNE * selectedLeverage) / P : 0;
            if (P > 0 && Number.isFinite(sellMaxBase) && Number.isFinite(baseQty) && baseQty > sellMaxBase) {
                const maxAmt = isQuoteSize
                    ? sellMaxBase * P
                    : snapToIncrement(sellMaxBase, step_size, "floor");
                showFieldError(side, "amount", amountLimitLabel("Max", maxAmt, isQuoteSize ? tick_size : step_size, isQuoteSize ? SelectedCoin?.quote_currency : SelectedCoin?.base_currency));
                return;
            }
        }

        // For MARKET BUY: send quote_quantity only when the user entered a USDT amount.
        // When the user entered base quantity, send quantity instead (API spec §MARKET BUY).
        const marketBuyQuoteQty =
            t === "MARKET" && side === "BUY" && isQuoteSize
                ? parseOrderQty(raw)
                : null;

        const payload = buildSpotOrderPayload(side, t, priceForApi, baseQty, extras, marketBuyQuoteQty, selectedLeverage);
        if (!payload.pair) {
            alertErrorMessage("Select a trading pair");
            return;
        }
        setPlacingOrderSide(side);
        try {
            const result = await AuthService.marginPlaceOrder(payload);
            if (result?.success) {
                alertSuccessMessage("Order Placed Successfully!!");
                subscribeToExchange(SelectedCoin?.base_currency_id, SelectedCoin?.quote_currency_id, { tradeType: "margin", pairId: SelectedCoin?._id });
                if (selectedPairApiMargin) {
                    AuthService.marginOpenOrders({ pair: selectedPairApiMargin }).then((res) => {
                        const items = res?.success
                            ? (Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []))
                            : [];
                        setMarginOpenOrdersRows(items);
                    }).catch(() => {});
                }
            } else {
                const code = result?.code || result?.error;
                const msg = MARGIN_ORDER_ERROR_MESSAGES[code] || result?.message || "Failed to place order.";
                alertErrorMessage(msg);
            }
        } catch {
            alertErrorMessage("Something went wrong. Please try again.");
        } finally {
            setPlacingOrderSide("");
        }
    };

    // Check if a value being typed could lead to a valid price >= tick_size
    const isValidPriceInput = (value) => {
        const valueClean = String(value).replace(/,/g, '');
        if (valueClean === '' || valueClean === '0') return true;
        const tickSize = SelectedCoin?.tick_size || 0.01;
        const pricePrecision = getPricePrecision();
        const regex = new RegExp(`^\\d*\\.?\\d{0,${pricePrecision}}$`);
        if (!regex.test(valueClean)) return false;
        if (valueClean.endsWith('.')) return true;
        const numValue = parseFloat(valueClean);
        if (isNaN(numValue)) return false;
        if (numValue === 0) return true;
        return numValue >= tickSize;
    };

    const isValidQuantityInput = (value, { isQuoteAmount = false } = {}) => {
        if (value === '' || value === '0') return true;
        const stepSize = SelectedCoin?.step_size || 0.00001;
        const quoteDec = SelectedCoin?.quote_decimal ?? SelectedCoin?.quote_asset_precision ?? 8;
        const qtyPrecision = isQuoteAmount ? quoteDec : getQuantityPrecision();
        const regex = new RegExp(`^\\d*\\.?\\d{0,${qtyPrecision}}$`);
        if (!regex.test(value)) return false;
        if (value.endsWith('.')) return true;
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return false;
        if (numValue === 0) return true;
        if (isQuoteAmount) return numValue >= 0;
        return numValue >= stepSize;
    };

    // Handle price input - strictly block values below tick_size
    const handlePriceInput = (value, setter) => {
        const stripped = String(value).replace(/,/g, '');
        if (isValidPriceInput(stripped)) {
            setter(stripped);
        }
    };

    // Handle quantity input - strictly block values below step_size
    const handleQuantityInput = (value, setter, options) => {
        if (isValidQuantityInput(value, options)) {
            setter(value);
        }
    };

    // Round value to nearest step/tick on blur (cleanup)
    const handlePriceBlur = (value, setter) => {
        const v = String(value).replace(/,/g, '');
        if (v === '' || v === '0' || v === '0.') {
            setter('');
            return;
        }
        const tickSize = SelectedCoin?.tick_size || 0.01;
        const numValue = parseFloat(v);
        if (isNaN(numValue) || numValue === 0) {
            setter('');
            return;
        }
        // Ensure minimum tick_size
        if (numValue < tickSize) {
            setter(tickSize.toString());
            return;
        }
        // Round to nearest tick_size
        const rounded = Math.round(numValue / tickSize) * tickSize;
        const precision = getPricePrecision();
        setter(parseFloat(rounded.toFixed(precision)).toString());
    };

    const handleQuantityBlur = (value, setter, { isQuoteAmount = false } = {}) => {
        if (value === '' || value === '0' || value === '0.') {
            setter('');
            return;
        }
        const numValue = parseFloat(String(value).replace(/,/g, ""));
        if (isNaN(numValue) || numValue === 0) {
            setter('');
            return;
        }

        if (isQuoteAmount) {
            const quoteDec = Number(SelectedCoin?.quote_decimal ?? SelectedCoin?.quote_asset_precision ?? 2);
            const dec = Number.isFinite(quoteDec) && quoteDec >= 0 ? quoteDec : 2;
            setter(parseFloat(numValue.toFixed(dec)).toString());
            return;
        }

        const stepSize = Number(SelectedCoin?.step_size) || 0.00001;
        const minQty = Number(SelectedCoin?.min_order_qty) || stepSize;
        const precision = getQuantityPrecision();
        if (numValue < minQty) {
            setter(parseFloat(numValue.toFixed(precision)).toString());
            return;
        }
        const rounded = Math.round(numValue / stepSize) * stepSize;
        setter(parseFloat(rounded.toFixed(precision)).toString());
    };

    // Total field handlers — derive qty from total ÷ limit price
    const handleBuyTotalChange = (val) => {
        const total = parseFloat(val);
        const p = parsePriceNum(buyOrderPrice) || parsePriceNum(buyprice) || 0;
        if (Number.isFinite(total) && total > 0 && p > 0) {
            setbuyamount(String(ceilQuantityToStep(total / p)));
        } else if (!val || val === "0") {
            setbuyamount("");
        }
    };

    const handleSellTotalChange = (val) => {
        const total = parseFloat(val);
        const p = parsePriceNum(sellOrderPrice) || parsePriceNum(sellPrice) || 0;
        if (Number.isFinite(total) && total > 0 && p > 0) {
            setsellAmount(String(ceilQuantityToStep(total / p)));
        } else if (!val || val === "0") {
            setsellAmount("");
        }
    };

    const displaySellOrders = useMemo(() => {
        const rows = aggregateOrderBookRows(SellOrders, orderBookAggStep);
        const sorted = rows.sort((a, b) => b.price - a.price);
        return orderBookViewMode === "both" ? sorted.slice(-ORDER_BOOK_BOTH_DEPTH) : sorted;
    }, [SellOrders, orderBookAggStep, orderBookViewMode]);

    const displayBuyOrders = useMemo(() => {
        const rows = aggregateOrderBookRows(BuyOrders, orderBookAggStep);
        const sorted = rows.sort((a, b) => b.price - a.price);
        return orderBookViewMode === "both" ? sorted.slice(0, ORDER_BOOK_BOTH_DEPTH) : sorted;
    }, [BuyOrders, orderBookAggStep, orderBookViewMode]);

    const maxSellVolume = useMemo(
        () => Math.max(...displaySellOrders.map((order) => Number(order.remaining) || 0), 0),
        [displaySellOrders]
    );
    const maxBuyVolume = useMemo(
        () => Math.max(...displayBuyOrders.map((order) => Number(order.remaining) || 0), 0),
        [displayBuyOrders]
    );

    const orderBookBidAskRatio = useMemo(() => {
        const bid = displayBuyOrders.reduce((s, o) => s + (Number(o.remaining) || 0), 0);
        const ask = displaySellOrders.reduce((s, o) => s + (Number(o.remaining) || 0), 0);
        const t = bid + ask;
        if (t <= 0) return { bidPct: 50, askPct: 50 };
        return { bidPct: (bid / t) * 100, askPct: (ask / t) * 100 };
    }, [displayBuyOrders, displaySellOrders]);

    /** Only auto-scroll to best ask on first data for a pair, or when base/quote changes (not on every book update). */
    const lastAskScrollPairKeyRef = useRef(null);
    const askScrollPairKey = baseCurId && quoteCurId ? `${baseCurId}|${quoteCurId}` : null;

    /** Last price for header + order book mid: stream when connected, else pair snapshot. */
    const displayLastForHeader = useMemo(() => {
        return lastMarketPrice !== "" && lastMarketPrice != null ? lastMarketPrice : buyprice;
    }, [lastMarketPrice, buyprice]);

    useEffect(() => {
        const livePrice = lastMarketPrice || buyprice;
        if (!livePrice) return;
        const p = parseFloat(livePrice);
        if (!Number.isFinite(p) || p <= 0) return;
        setbuyOrderPrice((prev) => prev === '' ? String(p) : prev);
        setsellOrderPrice((prev) => prev === '' ? String(p) : prev);
    }, [lastMarketPrice, buyprice]);

    useEffect(() => {
        if (!askScrollPairKey) return;
        if (marginTpSlPairKeySeenRef.current === askScrollPairKey) return;
        marginTpSlPairKeySeenRef.current = askScrollPairKey;
        setMarginFormTpSl({
            tpTriggerPrice: "",
            tpLimitMarket: true,
            tpLimitPrice: "",
            slTriggerPrice: "",
            slLimitMarket: true,
            slLimitPrice: "",
            quantity: "",
            amountPercent: 0,
        });
        setMarginFormTwap({
            totalQty: "",
            totalPercent: 0,
            durationHours: "",
            durationMinutes: "",
            priceVariance: "",
            priceVarianceUnit: "%",
            activationPrice: "",
            timeInterval: "",
            timeIntervalUnit: "Hour",
            perSubOrderQty: "",
        });
        setMarginFormIcebergPro({
            priceVariance: "",
            priceVarianceUnit: "%",
            activationPrice: "",
            totalQty: "",
            perSubOrderQty: "",
        });
        setMarginFormLoopOrder({
            price: "",
            priceMode: "LAST_PRICE",
            takeProfitPct: "",
            stopLossPct: "",
            pauseAfterSlMin: "",
            amount: "",
            totalLoops: "",
            validFor: "1Day",
        });
        setMarginFormTrailingStop({
            pullbackRatePct: "",
            pullbackUnit: "%",
            activationPrice: "",
            amount: "",
            amountPercent: 0,
            validFor: "1Day",
        });
        setMarginFormConditional({
            triggerPrice: "",
            orderMarket: true,
            orderLimitPrice: "",
            amount: "",
            amountPercent: 0,
            validFor: "Permanent",
        });
    }, [askScrollPairKey]);

    useEffect(() => {
        if (infoPlaceOrder !== "TP_SL") return;
        const raw = displayLastForHeader;
        if (raw === "" || raw == null) return;
        const num = Number(String(raw).replace(/,/g, ""));
        if (!Number.isFinite(num) || num === 0) return;
        const s = String(raw).replace(/,/g, "");
        setMarginFormTpSl((prev) => {
            const next = { ...prev };
            let changed = false;
            if (prev.tpTriggerPrice === "") {
                next.tpTriggerPrice = s;
                changed = true;
            }
            if (prev.slTriggerPrice === "") {
                next.slTriggerPrice = s;
                changed = true;
            }
            if (!prev.tpLimitMarket && prev.tpLimitPrice === "") {
                next.tpLimitPrice = s;
                changed = true;
            }
            if (!prev.slLimitMarket && prev.slLimitPrice === "") {
                next.slLimitPrice = s;
                changed = true;
            }
            return changed ? next : prev;
        });
    }, [infoPlaceOrder, displayLastForHeader]);

    // Sell/ask list: sorted high → low, so best (lowest) ask is the last row — pin scroll to bottom
    useLayoutEffect(() => {
        if (!orderBookSocketReady) return;
        if (orderBookViewMode === "bids") return;
        if (!askScrollPairKey) return;
        if (!displaySellOrders?.length) return;
        if (lastAskScrollPairKeyRef.current === askScrollPairKey) return;
        const run = () => {
            const nodes = document.querySelectorAll(".orderbook-asks-sell-list");
            if (nodes.length === 0) return;
            nodes.forEach((el) => {
                const top = el.scrollHeight - el.clientHeight;
                if (top > 0) el.scrollTop = top;
            });
            lastAskScrollPairKeyRef.current = askScrollPairKey;
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
    }, [askScrollPairKey, displaySellOrders?.length, orderBookViewMode, orderBookSocketReady]);

    const formatOrderBookAggPrice = (price) => {
        if (price === undefined || price === null || Number.isNaN(Number(price))) return "0";
        const n = Number(price);
        const step = Number(orderBookAggStep);
        if (Number.isFinite(step) && step >= 1) return String(Math.round(n));
        const dp = step > 0 && step < 1 ? Math.min(8, getDecimalPlaces(step) + 1) : 1;
        return parseFloat(n.toFixed(dp)).toString();
    };

    const formatPriceThousands = (raw) => {
        const n = parseFloat(String(raw).replace(/,/g, ""));
        if (Number.isNaN(n)) return raw === undefined || raw === null ? "" : String(raw);
        const prec = getPricePrecision();
        return n.toLocaleString("en-US", { maximumFractionDigits: prec, minimumFractionDigits: 0 });
    };

    const nudgeBuyOrderPrice = (direction) => {
        const tick = Number(SelectedCoin?.tick_size) || 0.01;
        const prec = getPricePrecision();
        const base = buyOrderPrice !== "" ? parseFloat(buyOrderPrice) : Number(buyprice) || 0;
        const n = Number.isFinite(base) ? base : 0;
        const next = Math.round((n + direction * tick) / tick) * tick;
        const clamped = Math.max(next, tick);
        const nextStr = parseFloat(clamped.toFixed(prec)).toString();
        setbuyOrderPrice(nextStr);
        if (infoPlaceOrder === "LIMIT" && buyQuoteMaxBal > 0) {
            const p = parseFloat(nextStr);
            if (p > 0 && limitBuyPercent > 0) {
                setbuyamount(toFixed8(((buyQuoteMaxBal / 100) * limitBuyPercent) / p));
            } else if (limitBuyPercent === 0) {
                setbuyamount("");
            }
        }
    };

    // Doc §17a: net_equity = (Qf−Qb) + (Bf−Bb)×P
    // Prefer socket net_equity; fall back to client formula using last price
    const _Qf = parseFloat(BuyCoinBal)  || 0;
    const _Bf = parseFloat(SellCoinBal) || 0;
    const _Qb = marginBalance.quoteBorrowed || 0;
    const _Bb = marginBalance.baseBorrowed  || 0;
    const _socketNE = marginBalance.netEquity;
    const _refP = Number(buyprice) || Number(sellPrice) || 0;
    const _netEquity = (_socketNE != null && Number.isFinite(_socketNE) && _socketNE >= 0)
        ? _socketNE
        : Math.max(0, (_Qf - _Qb) + (_Bf - _Bb) * _refP);
    const _L = leverageConfirmed || 1;
    const buyQuoteMaxBal = _Qf * _L;
    const marginQuoteMaxBal = _netEquity * _L;

    const applyLimitBuySlider = (pct) => {
        const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
        setLimitBuyPercent(safe);
        if (safe === 0) { setbuyamount(""); return; }
        const p = buyOrderPrice !== "" ? parseFloat(buyOrderPrice) : Number(buyprice) || 0;
        if (!p || !buyQuoteMaxBal) return;
        setbuyamount(toFixed8(((buyQuoteMaxBal / 100) * safe) / p));
    };

    const applyMarketBuySlider = (pct) => {
        const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
        setMarketBuyPercent(safe);
        if (safe === 0) { setbuyamount(""); return; }
        if (!buyQuoteMaxBal) return;
        const buffer = safe === 100 ? 1.001 : 1;
        if (buyAmountDenom === "QUOTE") {
            setbuyamount(toFixed8((buyQuoteMaxBal / 100) * safe / buffer));
            return;
        }
        const p = Number(lastMarketPrice) || Number(buyprice) || 0;
        if (!p) return;
        setbuyamount(toFixed8(((buyQuoteMaxBal / 100) * safe) / p / buffer));
    };

    const applyMarketSellSlider = (pct) => {
        const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
        setMarketSellPercent(safe);
        if (safe === 0) { setsellAmount(""); return; }
        const p = Number(lastMarketPrice) || Number(sellPrice) || 0;
        if (sellAmountDenom === "QUOTE") {
            if (!marginQuoteMaxBal) return;
            setsellAmount(toFixed8((marginQuoteMaxBal / 100) * safe));
            return;
        }
        if (!p || !marginQuoteMaxBal) return;
        const sellMaxBNB = marginQuoteMaxBal / p;
        setsellAmount(toFixed8((sellMaxBNB / 100) * safe));
    };

    const applyLimitSellSlider = (pct) => {
        const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
        setLimitSellPercent(safe);
        if (safe === 0) { setsellAmount(""); return; }
        const p = parseFloat(sellOrderPrice) || Number(sellPrice) || 0;
        if (!p || !marginQuoteMaxBal) return;
        const sellMaxBNB = marginQuoteMaxBal / p;
        setsellAmount(toFixed8((sellMaxBNB / 100) * safe));
    };

    const applyStopBuySlider = (pct) => {
        const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
        setStopBuyPercent(safe);
        if (safe === 0) { setbuyamount(""); return; }
        if (!buyQuoteMaxBal) return;
        const buffer = safe === 100 && infoPlaceOrder === "STOP_MARKET" ? 1.001 : 1;
        if (buyAmountDenom === "QUOTE") {
            setbuyamount(toFixed8((buyQuoteMaxBal / 100) * safe / buffer));
            return;
        }
        const refPrice =
            infoPlaceOrder === "STOP_LIMIT"
                ? (buyOrderPrice !== "" ? parseFloat(buyOrderPrice) : Number(buyprice) || 0)
                : (Number(buyprice) || 0);
        if (!refPrice) return;
        setbuyamount(toFixed8(((buyQuoteMaxBal / 100) * safe) / refPrice / buffer));
    };

    const applyStopSellSlider = (pct) => {
        const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
        setStopSellPercent(safe);
        if (safe === 0) { setsellAmount(""); return; }
        if (!marginQuoteMaxBal) return;
        const refPrice =
            infoPlaceOrder === "STOP_LIMIT"
                ? (sellOrderPrice !== "" ? parseFloat(sellOrderPrice) : Number(sellPrice) || 0)
                : (Number(sellPrice) || 0);
        if (sellAmountDenom === "QUOTE") {
            setsellAmount(toFixed8((marginQuoteMaxBal / 100) * safe));
            return;
        }
        if (!refPrice) return;
        const sellMaxBNB = marginQuoteMaxBal / refPrice;
        setsellAmount(toFixed8((sellMaxBNB / 100) * safe));
    };

    const nudgeSellOrderPrice = (direction) => {
        const tick = Number(SelectedCoin?.tick_size) || 0.01;
        const prec = getPricePrecision();
        const base = sellOrderPrice !== "" ? parseFloat(sellOrderPrice) : Number(sellPrice) || 0;
        const n = Number.isFinite(base) ? base : 0;
        const next = Math.round((n + direction * tick) / tick) * tick;
        const clamped = Math.max(next, tick);
        setsellOrderPrice(parseFloat(clamped.toFixed(prec)).toString());
    };

    const isStopOrder = marginOrderUsesStopTriggerField(infoPlaceOrder);
    const showBuyAmtDenomSelect = marginShowAmtDenomSelect(infoPlaceOrder);
    const showSellAmtDenomSelect =
        infoPlaceOrder === "STOP_MARKET" ||
        infoPlaceOrder === "STOP_LIMIT";
    const isLimitBuyUi = infoPlaceOrder === "LIMIT" && !isStopOrder;
    const showSpotOrderFooter = MARGIN_CLASSIC_SPOT_FORM_KINDS.has(infoPlaceOrder);

    const marginAdvancedFormsBundle = useMemo(
        () => ({
            conditional: { value: marginFormConditional, set: setMarginFormConditional },
            tpSl: { value: marginFormTpSl, set: setMarginFormTpSl },
            twap: { value: marginFormTwap, set: setMarginFormTwap },
            icebergPro: { value: marginFormIcebergPro, set: setMarginFormIcebergPro },
            loopOrder: { value: marginFormLoopOrder, set: setMarginFormLoopOrder },
            trailingStop: { value: marginFormTrailingStop, set: setMarginFormTrailingStop },
            advancedLimit: { value: marginFormAdvancedLimit, set: setMarginFormAdvancedLimit },
            pov: { value: marginFormPov, set: setMarginFormPov },
        }),
        [
            marginFormConditional,
            marginFormTpSl,
            marginFormTwap,
            marginFormIcebergPro,
            marginFormLoopOrder,
            marginFormTrailingStop,
            marginFormAdvancedLimit,
            marginFormPov,
        ]
    );

    return (
        <FuturesTradeTransferProvider isMargin={true}>
            <>
                <Helmet>
                    <title>{`${SelectedCoin?.base_currency || "BTC"}/${SelectedCoin?.quote_currency || "USDT"} Margin Trading – AGCX`}</title>

                    <meta
                        name="description"
                        content="Margin trading on AGCX with live market data and the same interface as spot. Register today."
                    />

                    <meta
                        name="keywords"
                        content="margin trading, bitcoin usdt, AGCX margin, BTC USDT AGCX"
                    />
                </Helmet>



                <div className="trade-wrapper spot pb-3 ">
                    <div className="  container-fluid">
                        <div className="row g-1 g-md-2" >

                            <TradePairsSection
                                setIsFavouritesOpen={setIsFavouritesOpen}
                                isFavouritesOpen={isFavouritesOpen}
                                token={token}
                                coinFilter={coinFilter}
                                setcoinFilter={setcoinFilter}
                                CoinPairDetails={CoinPairDetails}
                                search={search}
                                setsearch={setsearch}
                                favCoins={favCoins}
                                SelectedCoin={SelectedCoin}
                                handleSelectCoin={handleSelectCoin}
                                coinIconSrc={coinIconSrc}
                                handleAddFav={handleAddFav}
                            />

                            <TradeCenterSection
                                setShowCoinList={setShowCoinList}
                                setShowMobileFavouritesPopup={setShowMobileFavouritesPopup}
                                setIsFavouritesOpen={setIsFavouritesOpen}
                                showCoinList={showCoinList}
                                showTab={showTab}
                                setShowTab={setShowTab}
                                getDescAndLink={getDescAndLink}
                                SelectedCoin={SelectedCoin}
                                coinIconSrc={coinIconSrc}
                                isPricePositive={isPricePositive}
                                displayLastForHeader={displayLastForHeader}
                                priceChange={priceChange}
                                changesHour={changesHour}
                                priceHigh={priceHigh}
                                priceLow={priceLow}
                                volume={volume}
                                desAndLinks={desAndLinks}
                                orderBookActiveTab={orderBookActiveTab}
                                setOrderBookActiveTab={setOrderBookActiveTab}
                                orderBookViewMode={orderBookViewMode}
                                setOrderBookViewMode={setOrderBookViewMode}
                                orderBookAggOpen={orderBookAggOpen}
                                setOrderBookAggOpen={setOrderBookAggOpen}
                                orderBookAggStep={orderBookAggStep}
                                orderBookAggOptions={orderBookAggOptions}
                                setOrderBookAggStep={setOrderBookAggStep}
                                orderBookSwapAmountTotal={orderBookSwapAmountTotal}
                                setOrderBookSwapAmountTotal={setOrderBookSwapAmountTotal}
                                orderBookSocketReady={orderBookSocketReady}
                                displaySellOrders={displaySellOrders}
                                maxSellVolume={maxSellVolume}
                                setbuyamount={setbuyamount}
                                infoPlaceOrder={infoPlaceOrder}
                                setbuyOrderPrice={setbuyOrderPrice}
                                formatQuantity={formatQuantity}
                                formatPrice={formatPrice}
                                formatOrderBookAggPrice={formatOrderBookAggPrice}
                                formatOrderBookNotionalShort={formatOrderBookNotionalShort}
                                displayBuyOrders={displayBuyOrders}
                                maxBuyVolume={maxBuyVolume}
                                setsellAmount={setsellAmount}
                                setsellOrderPrice={setsellOrderPrice}
                                orderBookBidAskRatio={orderBookBidAskRatio}
                                formatPriceThousands={formatPriceThousands}
                                RecentTrade={RecentTrade}
                                fetchSpotWallets={fetchSpotWallets}
                                token={token}
                                walletsLoading={walletsLoading}
                                spotWallets={spotWallets}
                                setShowBuySellTab={setShowBuySellTab}
                                showBuySellTab={showBuySellTab}
                                isConditionalMenuOpen={isConditionalMenuOpen}
                                setIsConditionalMenuOpen={setIsConditionalMenuOpen}
                                applySpotOrderType={applySpotOrderType}
                                orderFieldError={orderFieldError}
                                isSpotDisabled={isSpotDisabled}
                                navigate={navigate}
                                isStopOrder={isStopOrder}
                                buyStopPrice={buyStopPrice}
                                setBuyStopPrice={setBuyStopPrice}
                                buyOrderPrice={buyOrderPrice}
                                nudgeBuyOrderPrice={nudgeBuyOrderPrice}
                                nudgeSellOrderPrice={nudgeSellOrderPrice}
                                priceFieldFocus={priceFieldFocus}
                                setPriceFieldFocus={setPriceFieldFocus}
                                handlePriceInput={handlePriceInput}
                                handlePriceBlur={handlePriceBlur}
                                buyamount={buyamount}
                                handleQuantityInput={handleQuantityInput}
                                handleQuantityBlur={handleQuantityBlur}
                                handleBuyTotalChange={handleBuyTotalChange}
                                handleSellTotalChange={handleSellTotalChange}
                                showBuyAmtDenomSelect={showBuyAmtDenomSelect}
                                showSellAmtDenomSelect={showSellAmtDenomSelect}
                                buyAmountDenom={buyAmountDenom}
                                setbuyAmountDenom={setbuyAmountDenom}
                                buyprice={buyprice}
                                setBuySlippageEnabled={setBuySlippageEnabled}
                                buySlippageEnabled={buySlippageEnabled}
                                setBuySlippageInput={setBuySlippageInput}
                                buySlippageInput={buySlippageInput}
                                buySlippageError={buySlippageError}
                                slippagePlaceholder={slippagePlaceholder}
                                isLimitBuyUi={isLimitBuyUi}
                                limitBuyPercent={limitBuyPercent}
                                applyLimitBuySlider={applyLimitBuySlider}
                                marketBuyPercent={marketBuyPercent}
                                applyMarketBuySlider={applyMarketBuySlider}
                                stopBuyPercent={stopBuyPercent}
                                applyStopBuySlider={applyStopBuySlider}
                                sellStopPrice={sellStopPrice}
                                setSellStopPrice={setSellStopPrice}
                                sellOrderPrice={sellOrderPrice}
                                sellAmount={sellAmount}
                                sellAmountDenom={sellAmountDenom}
                                setsellAmountDenom={setsellAmountDenom}
                                sellPrice={sellPrice}
                                setSellSlippageEnabled={setSellSlippageEnabled}
                                sellSlippageEnabled={sellSlippageEnabled}
                                setSellSlippageInput={setSellSlippageInput}
                                sellSlippageInput={sellSlippageInput}
                                sellSlippageError={sellSlippageError}
                                limitSellPercent={limitSellPercent}
                                applyLimitSellSlider={applyLimitSellSlider}
                                marketSellPercent={marketSellPercent}
                                applyMarketSellSlider={applyMarketSellSlider}
                                stopSellPercent={stopSellPercent}
                                applyStopSellSlider={applyStopSellSlider}
                                showSpotOrderFooter={showSpotOrderFooter}
                                marginAdvancedFormsBundle={marginAdvancedFormsBundle}
                                advLimitPriceMeta={{
                                    tick: Number(SelectedCoin?.tick_size) || 0.01,
                                    prec: getPricePrecision(),
                                }}
                                parseOrderQty={parseOrderQty}
                                formatTotal={formatTotal}
                                placeSpotOrder={placeSpotOrder}
                                placingOrderSide={placingOrderSide}
                                KycStatus={KycStatus}
                                BuyCoinBal={BuyCoinBal}
                                SellCoinBal={SellCoinBal}
                                marginBalance={marginBalance}
                                limitBuyIoc={limitBuyIoc}
                                setLimitBuyIoc={setLimitBuyIoc}
                                limitBuyFok={limitBuyFok}
                                setLimitBuyFok={setLimitBuyFok}
                                limitSellIoc={limitSellIoc}
                                setLimitSellIoc={setLimitSellIoc}
                                limitSellFok={limitSellFok}
                                setLimitSellFok={setLimitSellFok}
                                OrderBookHeaderRow={OrderBookHeaderRow}
                                OrderBookAskRow={OrderBookAskRow}
                                OrderBookBidRow={OrderBookBidRow}
                                OrderBookMidPriceRow={OrderBookMidPriceRow}
                                OrderBookBidAskFooter={OrderBookBidAskFooter}
                                currentMarginAccount={currentMarginAccount}
                                leverageConfirmed={leverageConfirmed}
                                setLeverageConfirmed={setLeverageConfirmed}
                                leverageDraft={leverageDraft}
                                setLeverageDraft={setLeverageDraft}
                            />
                            <TradeHistorySection
                                positionOrderTab={positionOrderTab}
                                setPositionOrderTab={setPositionOrderTab}
                                SPOT_OPEN_ORDER_KINDS={SPOT_OPEN_ORDER_KINDS}
                                openOrderKindTab={openOrderKindTab}
                                setOpenOrderKindTab={setOpenOrderKindTab}
                                orderType={orderType}
                                setorderType={setorderType}
                                openOrders={marginOpenOrdersRows}
                                matchesOpenOrderKind={matchesOpenOrderKind}
                                getOpenOrderPair={getOpenOrderPair}
                                formatSpotOpenOrderDateTime={formatSpotOpenOrderDateTime}
                                spotOpenOrderTypeLabel={spotOpenOrderTypeLabel}
                                spotOpenOrderTifLabel={spotOpenOrderTifLabel}
                                safeToFixed8={safeToFixed8}
                                getOpenOrderStopPrice={getOpenOrderStopPrice}
                                nineDecimalFormat={nineDecimalFormat}
                                spotOpenOrderFilledQty={spotOpenOrderFilledQty}
                                spotOpenOrderRemainingQty={spotOpenOrderRemainingQty}
                                cancelOrder={cancelOrder}
                                tradeHistorySideFilter={tradeHistorySideFilter}
                                setTradeHistorySideFilter={setTradeHistorySideFilter}
                                thPreset={thPreset}
                                setThPreset={setThPreset}
                                thStartDate={thStartDate}
                                setThStartDate={setThStartDate}
                                thEndDate={thEndDate}
                                setThEndDate={setThEndDate}
                                applyThPreset={applyThPreset}
                                myTrades={marginTradeHistoryRows}
                                spotHistoryViewportKey={selectedPairApiMargin}
                                onSpotOrderHistoryScrollViewport={handleMarginOrderHistoryScroll}
                                onSpotTradeHistoryScrollViewport={handleMarginTradeHistoryScroll}
                                tradeHistoryBaseAsset={tradeHistoryBaseAsset}
                                tradeHistoryQuoteAsset={tradeHistoryQuoteAsset}
                                formatTradeHistoryDateTime={formatTradeHistoryDateTime}
                                tradeHistoryMarketLabel={tradeHistoryMarketLabel}
                                tradeHistoryRoleLabel={tradeHistoryRoleLabel}
                                pastOrderType={pastOrderType}
                                setpastOrderType={setpastOrderType}
                                pastOrders={pastOrders}
                                ohSideFilter={ohSideFilter}
                                setOhSideFilter={setOhSideFilter}
                                ohPreset={ohPreset}
                                setOhPreset={setOhPreset}
                                ohStartDate={ohStartDate}
                                setOhStartDate={setOhStartDate}
                                ohEndDate={ohEndDate}
                                setOhEndDate={setOhEndDate}
                                applyOhPreset={applyOhPreset}
                                orderHistoryQuoteCurrency={orderHistoryQuoteCurrency}
                                orderHistoryMarketLabel={orderHistoryMarketLabel}
                                orderHistoryExecutedValueNum={orderHistoryExecutedValueNum}
                                orderHistoryStatusLabel={orderHistoryStatusLabel}
                                orderHistoryStatusClass={orderHistoryStatusClass}
                                spotOrderLifecycleStatus={spotOrderLifecycleStatus}
                                expandedRowIndex={expandedRowIndex}
                                setExpandedRowIndex={setExpandedRowIndex}
                                formatOrderHistoryDateTime={formatOrderHistoryDateTime}
                                showExecutedTrades={showExecutedTrades}
                                setShowExecutedTrades={setShowExecutedTrades}
                                fetchSpotWallets={fetchSpotWallets}
                                SelectedCoin={SelectedCoin}
                                token={token}
                                BuyCoinBal={BuyCoinBal}
                                SellCoinBal={SellCoinBal}
                                marginBalance={marginBalance}
                                walletsLoading={walletsLoading}
                                spotWallets={spotWallets}
                                coinIconSrc={coinIconSrc}
                                marginPositionData={marginPositionData}
                                marginAccounts={allMarginAccounts}
                                marginPositionHistoryRows={marginPositionHistoryRows}
                                posHistSideFilter={posHistSideFilter}
                                setPosHistSideFilter={setPosHistSideFilter}
                                posHistStartDate={posHistStartDate}
                                setPosHistStartDate={setPosHistStartDate}
                                posHistEndDate={posHistEndDate}
                                setPosHistEndDate={setPosHistEndDate}
                                posHistPreset={posHistPreset}
                                setPosHistPreset={setPosHistPreset}
                                selectedPairApiMargin={selectedPairApiMargin}
                                formatTotal={formatTotal}
                                closePositionModal={closePositionModal}
                                setClosePositionModal={setClosePositionModal}
                                closePositionLoading={closePositionLoading}
                                closingPositionPairs={closingPositionPairs}
                                handleClosePosition={handleClosePosition}
                                loanRows={loanRows}
                                repayModalLoan={repayModalLoan}
                                setRepayModalLoan={setRepayModalLoan}
                                loanPairId={SelectedCoin?._id}
                                loanBaseCoin={SelectedCoin?.base_currency || ""}
                                loanQuoteCoin={SelectedCoin?.quote_currency || ""}
                                loanBuyCoinBal={BuyCoinBal}
                                loanSellCoinBal={SellCoinBal}
                                onLoanRefresh={() => {
                                    AuthService.marginLoans({})
                                        .then((r) => { if (r?.success) setLoanRows(Array.isArray(r.data) ? r.data : []); })
                                        .catch(() => {});
                                }}
                            />

                        </div>
                    </div>
                </div >

                {/* Mobile Favourites Popup */}
                {showMobileFavouritesPopup && (
                    <div className="mobile-favourites-popup-overlay" onClick={() => setShowMobileFavouritesPopup(false)}>
                        <div className="mobile-favourites-popup pairpopup" onClick={(e) => e.stopPropagation()}>
                            <div className="mobile-favourites-popup-header">
                                <h4>Select Pair</h4>
                                <button className="mobile-favourites-close-btn" onClick={() => setShowMobileFavouritesPopup(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="mobile-favourites-popup-content">
                                <div className="spotLists">
                                    {/* Search */}
                                    <div className="spot-list-search">
                                        <div className="ivu-input">
                                            <i className="ri-search-2-line"></i>
                                            <input
                                                autoComplete="off"
                                                spellCheck="false"
                                                type="search"
                                                placeholder="Search"
                                                onChange={(e) => setsearch(e.target.value)}
                                                value={search}
                                            />
                                        </div>
                                    </div>

                                    <ul className="favorites_list_tabs">
                                        {token && (
                                            <li>
                                                <button
                                                    className={coinFilter === 'FAV' ? 'active' : ''}
                                                    onClick={() => setcoinFilter('FAV')}
                                                >
                                                    Favourites
                                                </button>
                                            </li>
                                        )}
                                        {CoinPairDetails && [...new Set(CoinPairDetails.map(item => item?.quote_currency)), "BTC", "BNB", "ETH"].map((quoteCurrency, idx) => (
                                            <li key={idx}>
                                                <button
                                                    className={coinFilter === quoteCurrency ? 'active' : ''}
                                                    onClick={() => setcoinFilter(quoteCurrency)}
                                                >
                                                    {quoteCurrency}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Table */}
                                    <div className="price_card table-responsive">
                                        <table className="table table-sm table-borderless mb-0 orderbook-table">
                                            <thead>
                                                <tr>
                                                    <th>Pair</th>
                                                    <th className="text-end">Price</th>
                                                    <th className="text-end">Change</th>
                                                </tr>
                                            </thead>
                                            <tbody className="price_card_body">
                                                {CoinPairDetails &&
                                                    CoinPairDetails.map((data, index) => {
                                                        // Filter by favorites
                                                        if (coinFilter === "FAV" && !favCoins.includes(data?._id)) {
                                                            return null;
                                                        }
                                                        // Filter by quote currency
                                                        if (coinFilter !== "FAV" && (data?.quote_currency !== coinFilter && data?.base_currency !== coinFilter)) {
                                                            return null;
                                                        }

                                                        const isActive =
                                                            SelectedCoin?.base_currency === data?.base_currency &&
                                                            SelectedCoin?.quote_currency === data?.quote_currency;

                                                        return (
                                                            <tr
                                                                key={index}
                                                                className={isActive ? "active" : ""}
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => {
                                                                    handleSelectCoin(data);
                                                                    setShowMobileFavouritesPopup(false);
                                                                }}
                                                            >
                                                                {/* Pair */}
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <img
                                                                            src={coinIconSrc(data?.icon_path)}
                                                                            alt=""
                                                                            className="img-fluid me-1 round_img"
                                                                        />
                                                                        <div className="d-flex flex-column pair-color">
                                                                            {`${data?.base_currency}/${data?.quote_currency}`}
                                                                            <span className="tokensubcnt">{data?.base_currency_fullname}</span>
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* Price */}
                                                                <td className="text-end">
                                                                    <div className="d-flex flex-column pair-color">
                                                                        <span>{data?.buy_price}</span>
                                                                        <span className="tokensubcnt">${data?.buy_price}</span>
                                                                    </div>
                                                                </td>

                                                                {/* Change + Star */}
                                                                <td className="text-end">
                                                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                                                        <div className="d-flex flex-column pair-color text-end">
                                                                            <span
                                                                                className={
                                                                                    data?.change_percentage >= 0
                                                                                        ? "text-green"
                                                                                        : "text-danger"
                                                                                }
                                                                            >
                                                                                {data?.change_percentage >= 0 ? `+${Number(parseFloat(data?.change_percentage)?.toFixed(5))}` : Number(parseFloat(data?.change_percentage)?.toFixed(5))}%
                                                                            </span>
                                                                            <span className="tokensubcnt">{parseFloat(data?.change?.toFixed(5)) || 0}</span>
                                                                        </div>

                                                                        {token && (
                                                                            <i
                                                                                className={
                                                                                    favCoins.includes(data?._id)
                                                                                        ? "ri ri-star-fill ri-xl"
                                                                                        : "ri ri-star-line ri-xl"
                                                                                }
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleAddFav(data?._id);
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        </FuturesTradeTransferProvider>
    )
}

export default Margin;
